import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useAuth } from '../context/AuthContext';
import { useGuestPrompt } from '../context/GuestPromptContext';
import { useUI } from '../context/UIContext';
import { useTutorial, TUTORIAL_STEPS } from '../context/TutorialContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { sendTradeRequestNotification } from '../lib/notifications';
import { getNavigationInfo, updateContextIndex, clearCardNavigationContext, getNavCount } from '../lib/cardNavigationContext';
import { getCardPriceRow, getPriceForCard, getPriceDetails, isWithinCooldown, daysSinceRefresh,
         fetchAndCacheSingleCard, isGradedCard, formatPrice } from '../services/cardPriceCache';
import { isVintageSet } from '../utils/vintageDetection';
import CreatePostModal from '../components/modals/CreatePostModal';
import TutorialOverlay, { TutorialBackdrop } from '../components/TutorialOverlay';
import AddParentModal from '../components/AddParentModal';
import ChaseWaveImage from '../assets/images/ChaseWave.png';
import CardImage from '../components/CardImage';
import CardChaseLogo from '../assets/logo.png';
import ChefImage from '../assets/images/Chef.png';

// Icons
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ZoomInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
    <path d="M11 8v6" />
    <path d="M8 11h6" />
  </svg>
);

const GridIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

const MaximizeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const ChevronDownIcon = ({ style }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronLeftIcon = ({ size = 32, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, width: size, height: size }}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = ({ size = 32, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, width: size, height: size }}>
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const ShareIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

const FolderIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const TradeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const HeartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const HeartFilledIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const MoreVerticalIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="1.5"></circle>
    <circle cx="12" cy="12" r="1.5"></circle>
    <circle cx="12" cy="19" r="1.5"></circle>
  </svg>
);

// Card Options Menu Component
const CardOptionsMenu = ({ isOpen, onClose, isFollowing, onToggleFollow, onReportCard, onReportUser, onBlock, isOwnCard, theme }) => {
  if (!isOpen) return null;

  const m = menuStyles(theme);

  return (
    <>
      <div style={m.backdrop} onClick={onClose} />
      <div style={m.menu}>
        {!isOwnCard && (
          <>
            <button style={m.menuItem} onClick={() => { onToggleFollow(); onClose(); }}>
              <span style={m.menuItemText}>
                {isFollowing ? 'Unfollow User' : 'Follow User'}
              </span>
            </button>
            <div style={m.menuDivider} />
            <button style={m.menuItem} onClick={() => { onBlock(); onClose(); }}>
              <span style={m.menuItemText}>Block User</span>
            </button>
            <div style={m.menuDivider} />
          </>
        )}
        <button style={m.menuItemDanger} onClick={() => { onReportCard(); onClose(); }}>
          <span style={m.menuItemTextDanger}>Report Card</span>
        </button>
      </div>
    </>
  );
};

// Report Card Modal Component
const ReportCardModal = ({ isOpen, onClose, cardId, currentUserId, onSuccess, theme }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    { id: 'nsfw', label: 'NSFW Content', description: 'Adult or inappropriate content' },
    { id: 'wrong_content', label: 'Wrong Content', description: 'Not Pokemon/TCG related' },
    { id: 'spam', label: 'Spam', description: 'Repetitive or promotional content' },
    { id: 'scam', label: 'Scam', description: 'Fraudulent or deceptive content' },
    { id: 'other', label: 'Other', description: 'Another issue not listed' },
  ];

  const handleSubmit = async () => {
    if (!selectedReason || !currentUserId || !cardId) return;

    setSubmitting(true);
    try {
      // Insert report with pending status
      const { error } = await supabase
        .from('reports')
        .insert({
          reporter_id: currentUserId,
          reported_card_id: cardId,
          reason: selectedReason,
          description: description.trim() || null,
          status: 'pending',
        });

      if (error) {
        if (error.code === '23505') {
          alert('You have already reported this card.');
        } else {
          throw error;
        }
      } else {
        // Hide the card immediately for safety (admin will review)
        await supabase
          .from('cards')
          .update({ hidden: true })
          .eq('id', cardId);

        onSuccess && onSuccess();
        onClose();
        setSelectedReason(null);
        setDescription('');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      alert('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const s = reportStyles(theme);

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <h2 style={s.title}>Report Card</h2>
          <button style={s.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div style={s.content}>
          <p style={s.subtitle}>Why are you reporting this card?</p>

          <div style={s.reasonsList}>
            {reasons.map((reason) => (
              <button
                key={reason.id}
                style={{
                  ...s.reasonButton,
                  ...(selectedReason === reason.id ? s.reasonButtonSelected : {}),
                }}
                onClick={() => setSelectedReason(reason.id)}
              >
                <div style={s.reasonContent}>
                  <span style={{
                    ...s.reasonLabel,
                    ...(selectedReason === reason.id ? s.reasonLabelSelected : {}),
                  }}>{reason.label}</span>
                  <span style={s.reasonDescription}>{reason.description}</span>
                </div>
                <div style={{
                  ...s.radioOuter,
                  ...(selectedReason === reason.id ? s.radioOuterSelected : {}),
                }}>
                  {selectedReason === reason.id && <div style={s.radioInner} />}
                </div>
              </button>
            ))}
          </div>

          <div style={s.descriptionSection}>
            <label style={s.descriptionLabel}>Additional details (optional)</label>
            <textarea
              style={s.descriptionInput}
              placeholder="Tell us more about the issue..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <div style={s.footer}>
          <button style={s.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{
              ...s.submitButton,
              ...((!selectedReason || submitting) ? s.submitButtonDisabled : {}),
            }}
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

const menuStyles = (theme) => ({
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  menu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    overflow: 'hidden',
    minWidth: '150px',
    zIndex: 1000,
    boxShadow: theme.shadowCard || '0 4px 12px rgba(0, 0, 0, 0.4)',
  },
  menuItem: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  menuItemDanger: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  menuItemText: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
  },
  menuItemTextDanger: {
    color: theme.error,
    fontSize: '14px',
    fontWeight: '500',
  },
  menuDivider: {
    height: '1px',
    backgroundColor: theme.border,
  },
});

const reportStyles = (theme) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.overlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
  },
  modal: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: theme.bgSheet,
    borderRadius: '16px',
    border: `1px solid ${theme.border}`,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.border}`,
  },
  title: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: theme.bgCard,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  content: {
    padding: '20px',
  },
  subtitle: {
    color: theme.textMuted,
    fontSize: '14px',
    margin: '0 0 16px 0',
  },
  reasonsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  reasonButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textAlign: 'left',
  },
  reasonButtonSelected: {
    backgroundColor: theme.accentBg,
    borderColor: theme.accent,
  },
  reasonContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  reasonLabel: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
  },
  reasonLabelSelected: {
    color: theme.accent,
  },
  reasonDescription: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  radioOuter: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: `2px solid ${theme.borderHover}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioOuterSelected: {
    borderColor: theme.accent,
  },
  radioInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: theme.accent,
  },
  descriptionSection: {
    marginTop: '20px',
  },
  descriptionLabel: {
    display: 'block',
    color: theme.textMuted,
    fontSize: '13px',
    marginBottom: '8px',
  },
  descriptionInput: {
    width: '100%',
    padding: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    color: theme.text,
    fontSize: '14px',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    borderTop: `1px solid ${theme.border}`,
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.borderHover}`,
    borderRadius: '10px',
    color: theme.textMuted,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: theme.error,
    border: 'none',
    borderRadius: '10px',
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButtonDisabled: {
    backgroundColor: theme.textDisabled,
    cursor: 'not-allowed',
  },
});

const CardDetail = () => {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { requireAuth } = useGuestPrompt();
  const { searchExactCard } = useUI();
  const { currentStep, handleTradeRequestSent, handleFirstTradeRequestNext } = useTutorial();
  const { isViewerOnly, activeProfile, activeProfileId, userProfile } = useProfile();
  const { theme, isDark } = useTheme();
  const [showParentModal, setShowParentModal] = useState(false);

  // Check for URL params to open photo viewer - read immediately for initial state
  const viewParam = searchParams.get('view'); // 'zoom' or 'grid'
  const photoParam = searchParams.get('photo'); // index or 'back'

  const [card, setCard] = useState(null);
  const [cardOwner, setCardOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwnCard, setIsOwnCard] = useState(false);
  // Initialize photo viewer state directly from URL params to avoid flash
  const [photoViewerOpen, setPhotoViewerOpen] = useState(!!viewParam);
  const [photoViewMode, setPhotoViewMode] = useState(viewParam === 'grid' ? 'grid' : 'zoom');
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [isPinchZoomed, setIsPinchZoomed] = useState(false);
  const [initialViewHandled, setInitialViewHandled] = useState(false);
  const [cameFromExternal, setCameFromExternal] = useState(!!viewParam); // Track if opened via URL params
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportSuccessOpen, setReportSuccessOpen] = useState(false);
  const [detailsPopupOpen, setDetailsPopupOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [binderPickerOpen, setBinderPickerOpen] = useState(false);
  const [binders, setBinders] = useState([]);
  const [cardBinder, setCardBinder] = useState(null);
  const [selectedBinder, setSelectedBinder] = useState(null);
  const [previewBinder, setPreviewBinder] = useState(null);
  const [previewCards, setPreviewCards] = useState([]);
  const [userCards, setUserCards] = useState([]); // All user's cards for post modal
  const [cardPrice, setCardPrice] = useState(null);
  const [priceDetailsInfo, setPriceDetailsInfo] = useState(null);
  const [priceRow, setPriceRow] = useState(null);
  const [refreshingPrice, setRefreshingPrice] = useState(false);
  const pricingEnabled = activeProfile?.pricing_trigger && activeProfile?.pricing_enabled === true;
  const [pricingPrefs, setPricingPrefs] = useState(activeProfile?.pricing_prefs || { modern_range: '3m', vintage_method: 'top3' });
  const [showPrefsPicker, setShowPrefsPicker] = useState(false);

  const VINTAGE_LABELS = { top3: 'Top 3 (12mo)', low3: 'Low 3 (12mo)', '12avg': '12 Month Median', alltime: 'All Time Median' };
  const MODERN_LABELS = { '1m': '1 Month Median', '3m': '3 Month Median', '6m': '6 Month Median', '12m': '12 Month Median' };

  // Trade request state
  const [tradeRequestSent, setTradeRequestSent] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [tradeConfirmOpen, setTradeConfirmOpen] = useState(false);
  const [nearestLocation, setNearestLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);

  // Post/Repost state
  const [lastPostDate, setLastPostDate] = useState(null);
  const [daysUntilRepost, setDaysUntilRepost] = useState(0);
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [gradePickerOpen, setGradePickerOpen] = useState(false);
  const [savingGrade, setSavingGrade] = useState(false);
  const [gradeType, setGradeType] = useState('raw'); // 'raw' | 'graded'
  const [gradingCompany, setGradingCompany] = useState('PSA');
  const [customCompany, setCustomCompany] = useState(''); // For "Other" grading company
  const [selectedGrade, setSelectedGrade] = useState(null); // Pending selection before confirm

  // Variant picker state
  const [variantPickerOpen, setVariantPickerOpen] = useState(false);
  const [savingVariant, setSavingVariant] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [availableVariants, setAvailableVariants] = useState([]);

  // TCGdex/Wish List mode
  const isTcgCard = cardId?.startsWith('tcg_');
  const [isWished, setIsWished] = useState(false);
  const [wishListCount, setWishListCount] = useState(0);
  const [addingToWishList, setAddingToWishList] = useState(false);
  const [removingFromWishList, setRemovingFromWishList] = useState(false);

  // Card navigation context (for left/right arrows between cards)
  const [navInfo, setNavInfo] = useState({ hasPrev: false, hasNext: false, currentIndex: -1, total: 0 });

  // Update navigation info when card changes
  useEffect(() => {
    if (cardId) {
      const info = getNavigationInfo(cardId);
      setNavInfo(info);
    }
  }, [cardId]);

  // Navigate to previous/next card
  const handleNavigatePrev = () => {
    if (navInfo.hasPrev && navInfo.prevId) {
      updateContextIndex(navInfo.currentIndex - 1);
      // Preserve any URL params like view or photo
      const currentParams = searchParams.toString();
      navigate(`/card/${navInfo.prevId}${currentParams ? `?${currentParams}` : ''}`);
    }
  };

  const handleNavigateNext = () => {
    if (navInfo.hasNext && navInfo.nextId) {
      updateContextIndex(navInfo.currentIndex + 1);
      // Preserve any URL params like view or photo
      const currentParams = searchParams.toString();
      navigate(`/card/${navInfo.nextId}${currentParams ? `?${currentParams}` : ''}`);
    }
  };

  // Get contextual navigation text based on context type
  const getNavContextLabel = () => {
    switch (navInfo.contextType) {
      case 'collection':
        return 'Collection';
      case 'collection-set':
        return 'Set';
      case 'home-post':
        return 'Post';
      case 'wishlist':
        return 'Wish List';
      case 'wishlist-set':
        return 'Set';
      case 'binder':
        return 'Binder';
      default:
        return 'List';
    }
  };

  // Swipe handling for photo viewer
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (cardPhotos) => {
    if (!touchStartX.current || !touchEndX.current) return;

    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0 && selectedPhotoIndex < cardPhotos.length - 1) {
        // Swiped left - next photo
        setSelectedPhotoIndex(prev => prev + 1);
      } else if (diff < 0 && selectedPhotoIndex > 0) {
        // Swiped right - previous photo
        setSelectedPhotoIndex(prev => prev - 1);
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const goToPrevPhoto = () => {
    if (selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(prev => prev - 1);
    }
  };

  const goToNextPhoto = (cardPhotos) => {
    if (selectedPhotoIndex < cardPhotos.length - 1) {
      setSelectedPhotoIndex(prev => prev + 1);
    }
  };

  // Search for this exact card (name + set + number)
  const handleSearchThisCard = () => {
    if (card) {
      searchExactCard({
        name: card.name,
        set_name: card.set_name,
        set_number: card.set_number,
      });
      navigate('/home');
    }
  };

  useEffect(() => {
    const fetchCard = async () => {
      if (!cardId) return;
      setLoading(true);

      try {
        // Check if this is a TCGdex card for wish list
        if (isTcgCard) {
          // Parse tcg_setId_localId format
          const parts = cardId.replace('tcg_', '').split('_');
          const setId = parts.slice(0, -1).join('_');
          const localId = parts[parts.length - 1];
          const tcgdexCardId = `${setId}-${localId}`;

          // Fetch card directly (not entire set) and user data in parallel
          const [cardRes, wishDataResult, ownedResult] = await Promise.all([
            fetch(`https://api.tcgdex.net/v2/en/cards/${tcgdexCardId}`),
            activeProfile?.id ? supabase
              .from('wish_list')
              .select('tcgdex_card_id', { count: 'exact' })
              .eq('user_id', activeProfile.id) : Promise.resolve({ data: null, count: 0 }),
            activeProfile?.id ? supabase
              .from('cards')
              .select('id')
              .eq('user_id', activeProfile.id)
              .eq('card_id', tcgdexCardId)
              .maybeSingle() : Promise.resolve({ data: null }),
          ]);

          const tcgCard = await cardRes.json();

          if (tcgCard && tcgCard.name) {
            setCard({
              id: cardId,
              name: tcgCard.name,
              set_name: tcgCard.set?.name || 'Unknown Set',
              set_number: localId,
              digital_image_url: tcgCard.image ? `${tcgCard.image}/high.webp` : `https://assets.tcgdex.net/en/${setId}/${localId}/high.webp`,
              hp: tcgCard.hp,
              types: tcgCard.types,
              rarity: tcgCard.rarity,
              tcg_set_id: setId,
              tcg_local_id: localId,
            });
          }

          if (activeProfile?.id) {
            setCurrentUserId(activeProfile.id);
            setWishListCount(wishDataResult.count || 0);
            setIsWished(wishDataResult.data?.some(w => w.tcgdex_card_id === tcgdexCardId) || false);
            setIsOwnCard(!!ownedResult.data);
          }

          setLoading(false);
          return;
        }

        // Regular database card - fetch card AND user profile in parallel
        const [cardResult, profileResult] = await Promise.all([
          supabase.from('cards').select('*').eq('id', cardId).single(),
          user ? supabase.from('users').select('id').eq('auth_id', user.id).single() : Promise.resolve({ data: null }),
        ]);

        if (cardResult.error) throw cardResult.error;
        const cardData = cardResult.data;
        const profileData = profileResult.data;

        // Check if card is hidden (under review)
        if (cardData.hidden) {
          setCard({ ...cardData, isHidden: true });
          setLoading(false);
          return;
        }

        // Fetch owner info (needs card's user_id which we now have)
        let ownerData = null;
        if (cardData.user_id) {
          const { data } = await supabase
            .from('users')
            .select('id, username, avatar_url, latitude, longitude, location_sharing_enabled')
            .eq('id', cardData.user_id)
            .single();
          ownerData = data;
        }

        // Determine effective user ID
        const finalUserId = profileData
          ? (activeProfileId && activeProfileId !== profileData.id ? activeProfileId : profileData.id)
          : null;

        // Set essential state
        setCard(cardData);
        if (ownerData) setCardOwner(ownerData);
        if (finalUserId) {
          setCurrentUserId(finalUserId);
          setIsOwnCard(cardData.user_id === finalUserId);
        }

        // If no profile, show what we have
        if (!profileData) {
          setLoading(false);
          return;
        }

        // Fetch ALL secondary data in parallel BEFORE showing UI
        const secondaryQueries = [];

        // Wish list check
        if (cardData.card_id) {
          secondaryQueries.push(
            supabase
              .from('wish_list')
              .select('tcgdex_card_id', { count: 'exact' })
              .eq('user_id', finalUserId)
              .then(({ data, count }) => ({ type: 'wishlist', data, count }))
          );
        }

        if (cardData.user_id === finalUserId) {
          // Own card - fetch binders and post status
          secondaryQueries.push(
            supabase
              .from('binders')
              .select('*')
              .eq('user_id', finalUserId)
              .order('name')
              .then(({ data }) => ({ type: 'binders', data }))
          );

          if (cardData.binder_id) {
            secondaryQueries.push(
              supabase
                .from('binders')
                .select('*')
                .eq('id', cardData.binder_id)
                .single()
                .then(({ data }) => ({ type: 'currentBinder', data }))
            );
          }

          secondaryQueries.push(
            supabase
              .from('posts_cards')
              .select('posts!posts_cards_post_id_fkey(created_at, user_id)')
              .eq('card_id', cardId)
              .order('posts(created_at)', { ascending: false })
              .limit(1)
              .then(({ data }) => ({ type: 'postHistory', data }))
          );
        } else {
          // Not own card - check follow and trade status
          secondaryQueries.push(
            supabase
              .from('user_follows')
              .select('id')
              .eq('follower_id', finalUserId)
              .eq('following_id', cardData.user_id)
              .maybeSingle()
              .then(({ data }) => ({ type: 'follow', data }))
          );

          secondaryQueries.push(
            supabase
              .from('trade_requests')
              .select('id')
              .eq('requester_id', profileData.id)
              .eq('card_id', cardId)
              .in('status', ['pending', 'accepted'])
              .maybeSingle()
              .then(({ data }) => ({ type: 'tradeRequest', data }))
          );
        }

        // Run all secondary queries in parallel, then update state all at once
        const secondaryResults = await Promise.all(secondaryQueries);

        // Process results
        let newBinders = null;
        let newCardBinder = null;
        let newWishListCount = 0;
        let newIsWished = false;
        let newDaysUntilRepost = 0;
        let newLastPostDate = null;
        let newIsFollowing = false;
        let newTradeRequestSent = false;

        for (const result of secondaryResults) {
          switch (result.type) {
            case 'wishlist':
              newWishListCount = result.count || 0;
              newIsWished = result.data?.some(w => w.tcgdex_card_id === cardData.card_id) || false;
              break;
            case 'binders':
              newBinders = result.data;
              break;
            case 'currentBinder':
              newCardBinder = result.data;
              break;
            case 'postHistory':
              if (result.data?.length > 0 && result.data[0].posts) {
                const lastPost = result.data[0].posts;
                if (lastPost.user_id === finalUserId) {
                  newLastPostDate = new Date(lastPost.created_at);
                  const diffDays = Math.floor((Date.now() - newLastPostDate) / (1000 * 60 * 60 * 24));
                  newDaysUntilRepost = Math.max(0, 7 - diffDays);
                }
              }
              break;
            case 'follow':
              newIsFollowing = !!result.data;
              break;
            case 'tradeRequest':
              newTradeRequestSent = !!result.data;
              break;
          }
        }

        // Set all secondary state at once
        setWishListCount(newWishListCount);
        setIsWished(newIsWished);
        if (newBinders) setBinders(newBinders);
        if (newCardBinder) setCardBinder(newCardBinder);
        setLastPostDate(newLastPostDate);
        setDaysUntilRepost(newDaysUntilRepost);
        setIsFollowing(newIsFollowing);
        setTradeRequestSent(newTradeRequestSent);

        // NOW show the UI - all data is ready
        setLoading(false);
      } catch (err) {
        console.error('Error fetching card:', err);
        setLoading(false);
      }
    };

    fetchCard();
  }, [cardId, user, activeProfileId, isTcgCard, activeProfile?.id]);

  // Fetch price from card_prices cache
  useEffect(() => {
    if (!card?.name || !pricingEnabled) return;
    let cancelled = false;
    const loadPrice = async () => {
      const row = await getCardPriceRow(
        card.name,
        card.set_name || '',
        card.set_number || card.card_id?.split('-').pop() || '',
      );
      if (cancelled) return;
      if (row) {
        setPriceRow(row);
        const price = getPriceForCard(row.stats, card.grade || 'NM', card.set_name, null, row.evidence);
        if (price != null) {
          setCardPrice({ price });
        }
        setPriceDetailsInfo(getPriceDetails(row.stats, card.grade || 'NM', card.set_name, null, row.evidence));
      } else {
        // No cached row, fire-and-forget to populate
        fetchAndCacheSingleCard({
          name: card.name,
          setName: card.set_name || '',
          setNumber: card.set_number || card.card_id?.split('-').pop() || '',
          variant: card.variant || '',
          grade: card.grade || 'NM',
        }).then(async () => {
          if (cancelled) return;
          const newRow = await getCardPriceRow(
            card.name,
            card.set_name || '',
            card.set_number || card.card_id?.split('-').pop() || '',
            card.variant || '',
          );
          if (!cancelled && newRow) {
            setPriceRow(newRow);
            const price = getPriceForCard(newRow.stats, card.grade || 'NM', card.set_name, null, newRow.evidence);
            if (price != null) setCardPrice({ price });
            setPriceDetailsInfo(getPriceDetails(newRow.stats, card.grade || 'NM', card.set_name, null, newRow.evidence));
          }
        }).catch(() => {});
      }
    };
    loadPrice();
    return () => { cancelled = true; };
  }, [card?.name, card?.set_number, card?.set_name, card?.grade, card?.card_id, pricingEnabled]);

  // Ensure currentUserId, wishListCount, and isWished are set when activeProfile becomes available
  // This handles cases where the profile loads after the card data
  useEffect(() => {
    if (activeProfile?.id && !currentUserId) {
      setCurrentUserId(activeProfile.id);
      // Fetch wish list data if not already loaded
      const fetchWishListData = async () => {
        // Build tcgdex card ID for checking if wished
        let tcgdexCardId = null;
        if (card?.tcg_set_id && card?.tcg_local_id) {
          tcgdexCardId = `${card.tcg_set_id}-${card.tcg_local_id}`;
        } else if (card?.card_id) {
          tcgdexCardId = card.card_id;
        }

        const { data, count } = await supabase
          .from('wish_list')
          .select('tcgdex_card_id', { count: 'exact' })
          .eq('user_id', activeProfile.id);

        setWishListCount(count || 0);
        if (tcgdexCardId) {
          setIsWished(data?.some(w => w.tcgdex_card_id === tcgdexCardId) || false);
        }
      };
      fetchWishListData();
    }
  }, [activeProfile?.id, currentUserId, card]);

  // Wish list functions - works for both TCGdex cards and regular database cards
  const handleAddToWishList = async () => {
    if (requireAuth('save to your wish list')) return;
    // Use activeProfile.id as fallback if currentUserId not yet set
    const userId = currentUserId || activeProfile?.id;
    if (!userId || wishListCount >= 20) {
      console.log('[WishList] Cannot add - userId:', userId, 'wishListCount:', wishListCount);
      return;
    }

    // Determine card info based on card type
    let tcgdexCardId, setId, localId, cardName, setName, imageUrl;

    console.log('[WishList] Card type check:', { tcg_set_id: card?.tcg_set_id, card_id: card?.card_id, card });

    if (card?.tcg_set_id) {
      // TCGdex card (from wish list browser)
      setId = card.tcg_set_id;
      localId = card.tcg_local_id;
      tcgdexCardId = `${setId}-${localId}`;
      cardName = card.name;
      setName = card.set_name;
      imageUrl = card.digital_image_url?.replace('/high.webp', '') || null;
    } else if (card?.card_id) {
      // Regular database card - extract set_id and local_id from card_id
      // card_id format: "{setId}-{localId}" e.g., "base1-25"
      const parts = card.card_id.split('-');
      localId = parts.pop(); // Last part is localId
      setId = parts.join('-'); // Rest is setId (handles set IDs with dashes)
      tcgdexCardId = card.card_id;
      cardName = card.name;
      setName = card.set_name || 'Unknown Set';
      // For regular cards, we need to fetch the image URL from TCGdex
      imageUrl = null; // Will fetch below
    } else {
      return; // Can't add to wish list without card info
    }

    setAddingToWishList(true);
    try {
      // If no image URL, try to fetch from TCGdex
      if (!imageUrl && setId && localId) {
        try {
          const res = await fetch(`https://api.tcgdex.net/v2/en/cards/${setId}-${localId}`);
          if (res.ok) {
            const tcgData = await res.json();
            imageUrl = tcgData.image || null;
            if (!setName || setName === 'Unknown Set') {
              setName = tcgData.set?.name || setName;
            }
          }
        } catch (e) {
          console.log('Could not fetch TCGdex data for image');
        }
      }

      console.log('[WishList] Inserting:', { userId, tcgdexCardId, cardName, setId, setName });
      const { error } = await supabase
        .from('wish_list')
        .insert({
          user_id: userId,
          tcgdex_card_id: tcgdexCardId,
          card_name: cardName,
          set_id: setId,
          set_name: setName,
          variant: 'Normal',
          image_url: imageUrl,
          is_priority: true, // Auto-add to Top 20 (we already check count < 20 before allowing add)
        });

      if (error) {
        console.error('[WishList] Insert error:', error);
        throw error;
      }
      setIsWished(true);
      setWishListCount(prev => prev + 1);
    } catch (e) {
      console.error('Error adding to wish list:', e);
    } finally {
      setAddingToWishList(false);
    }
  };

  const handleRemoveFromWishList = async () => {
    // Use activeProfile.id as fallback if currentUserId not yet set
    const userId = currentUserId || activeProfile?.id;
    if (!userId) return;

    // Determine tcgdex_card_id based on card type
    let tcgdexCardId;
    if (card?.tcg_set_id) {
      tcgdexCardId = `${card.tcg_set_id}-${card.tcg_local_id}`;
    } else if (card?.card_id) {
      tcgdexCardId = card.card_id;
    } else {
      return;
    }

    setRemovingFromWishList(true);
    try {
      const { error } = await supabase
        .from('wish_list')
        .delete()
        .eq('user_id', userId)
        .eq('tcgdex_card_id', tcgdexCardId);

      if (error) throw error;
      setIsWished(false);
      setWishListCount(prev => prev - 1);
    } catch (e) {
      console.error('Error removing from wish list:', e);
    } finally {
      setRemovingFromWishList(false);
    }
  };

  // Handle URL params to open photo viewer
  useEffect(() => {
    if (!card || initialViewHandled || loading) return;

    if (viewParam) {
      // Build photos array inline
      const photos = [];
      if (card.digital_image_url) photos.push({ label: 'Digital' });
      if (card.original_image_url) photos.push({ label: 'Photo' });
      if (card.back_photo) photos.push({ label: 'Back' });
      if (card.front_top_photo) photos.push({ label: 'Front Top' });
      if (card.front_bottom_photo) photos.push({ label: 'Front Bottom' });
      if (card.back_top_photo) photos.push({ label: 'Back Top' });
      if (card.back_bottom_photo) photos.push({ label: 'Back Bottom' });

      setCameFromExternal(true); // Mark that we came here via URL params

      if (viewParam === 'grid') {
        setPhotoViewMode('grid');
        setPhotoViewerOpen(true);
      } else if (viewParam === 'zoom') {
        setPhotoViewMode('zoom');

        if (photoParam === 'back') {
          // Find the back photo index
          const backIndex = photos.findIndex(p => p.label === 'Back');
          setSelectedPhotoIndex(backIndex >= 0 ? backIndex : 0);
        } else if (photoParam) {
          const idx = parseInt(photoParam, 10);
          setSelectedPhotoIndex(isNaN(idx) ? 0 : idx);
        }

        setPhotoViewerOpen(true);
      }

      setInitialViewHandled(true);
    }
  }, [card, viewParam, photoParam, loading, initialViewHandled]);

  const handleBack = () => {
    // Get how many card navigations we've done and go back past all of them
    const navCount = getNavCount();
    clearCardNavigationContext();

    if (navCount > 0) {
      // Go back past all the card-to-card navigations plus the initial card view
      window.history.go(-(navCount + 1));
    } else {
      navigate(-1);
    }
  };

  // Lazy load user cards and open post modal
  const handleOpenPostModal = async () => {
    // Only fetch if we don't already have the cards
    if (userCards.length === 0 && currentUserId) {
      const { data: allCardsData } = await supabase
        .from('cards')
        .select('*')
        .eq('user_id', currentUserId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (allCardsData) {
        setUserCards(allCardsData);
      }
    }
    setPostModalOpen(true);
  };

  // Close photo viewer - go back if came from Home, otherwise just close
  const handleClosePhotoViewer = () => {
    if (cameFromExternal) {
      navigate(-1); // Go back to where we came from (Home)
    } else {
      setPhotoViewerOpen(false);
    }
  };

  // Toggle follow card owner
  const handleToggleFollow = async () => {
    if (requireAuth('follow this trader')) return;
    if (!currentUserId || !card?.user_id) return;

    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);

    try {
      if (wasFollowing) {
        await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', currentUserId)
          .eq('following_id', card.user_id);
      } else {
        await supabase
          .from('user_follows')
          .insert({
            follower_id: currentUserId,
            following_id: card.user_id,
          });
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
      setIsFollowing(wasFollowing);
    }
  };

  // Block card owner
  const handleBlockUser = async () => {
    if (requireAuth('block a user')) return;
    if (!currentUserId || !card?.user_id) return;

    try {
      const { error } = await supabase
        .from('user_blocks')
        .insert({
          blocker_id: currentUserId,
          blocked_id: card.user_id,
        });

      if (error) {
        if (error.code === '23505') {
          alert('User is already blocked');
        } else {
          throw error;
        }
      } else {
        alert('User blocked. You will no longer see their content.');
        navigate(-1);
      }
    } catch (err) {
      console.error('Error blocking user:', err);
      alert('Failed to block user');
    }
  };

  // Report card
  const handleReportCard = () => {
    if (requireAuth('report a card')) return;
    setReportModalOpen(true);
  };

  // Handle successful report
  const handleReportSuccess = () => {
    setReportSuccessOpen(true);
  };

  // Binder management
  const openBinderPicker = () => {
    setSelectedBinder(cardBinder || null);
    setPreviewBinder(null);
    setPreviewCards([]);
    setBinderPickerOpen(true);
  };

  const handleQuickView = async (binder) => {
    if (previewBinder?.id === binder.id) {
      // Toggle off
      setPreviewBinder(null);
      setPreviewCards([]);
      return;
    }

    setPreviewBinder(binder);

    try {
      const { data } = await supabase
        .from('cards')
        .select('id, digital_image_url, name')
        .eq('binder_id', binder.id)
        .is('deleted_at', null)
        .limit(9);

      setPreviewCards(data || []);
    } catch (err) {
      console.error('Error fetching preview:', err);
      setPreviewCards([]);
    }
  };

  const handleBinderDone = async () => {
    setBinderPickerOpen(false);

    if (!selectedBinder && cardBinder) {
      // Removing from binder
      const oldBinder = cardBinder;
      setCardBinder(null);
      setCard(prev => ({ ...prev, binder_id: null }));

      try {
        await supabase.from('card_binders').delete()
          .eq('card_id', cardId).eq('binder_id', oldBinder.id);
        await supabase.from('cards').update({ binder_id: null }).eq('id', cardId);
      } catch (err) {
        console.error('Error removing from binder:', err);
      }
    } else if (selectedBinder && selectedBinder.id !== cardBinder?.id) {
      // Adding or changing binder
      const oldBinder = cardBinder;
      setCardBinder(selectedBinder);
      setCard(prev => ({ ...prev, binder_id: selectedBinder.id }));

      try {
        if (oldBinder) {
          await supabase.from('card_binders').delete()
            .eq('card_id', cardId).eq('binder_id', oldBinder.id);
        }
        await supabase.from('card_binders').insert({ card_id: cardId, binder_id: selectedBinder.id });
        await supabase.from('cards').update({ binder_id: selectedBinder.id }).eq('id', cardId);
      } catch (err) {
        console.error('Error updating binder:', err);
      }
    }

    setSelectedBinder(null);
    setPreviewBinder(null);
    setPreviewCards([]);
  };

  const handleRemoveFromBinder = async () => {
    if (!cardBinder) return;

    const oldBinder = cardBinder;
    setCardBinder(null);
    setCard(prev => ({ ...prev, binder_id: null }));

    try {
      await supabase.from('card_binders').delete()
        .eq('card_id', cardId).eq('binder_id', oldBinder.id);
      await supabase.from('cards').update({ binder_id: null }).eq('id', cardId);
    } catch (err) {
      console.error('Error removing from binder:', err);
    }
  };

  // Grade/Condition options
  const rawConditions = [
    { value: 'NM', label: 'Near Mint (NM)' },
    { value: 'LP', label: 'Lightly Played (LP)' },
    { value: 'MP', label: 'Moderately Played (MP)' },
    { value: 'HP', label: 'Heavily Played (HP)' },
    { value: 'DMG', label: 'Damaged (DMG)' },
  ];

  const psaGrades = [
    { value: 'PSA 10', label: 'PSA 10 - Gem Mint' },
    { value: 'PSA 9', label: 'PSA 9 - Mint' },
    { value: 'PSA 8', label: 'PSA 8 - NM-MT' },
    { value: 'PSA 7', label: 'PSA 7 - NM' },
    { value: 'PSA 6', label: 'PSA 6 - EX-MT' },
    { value: 'PSA 5', label: 'PSA 5 - EX' },
  ];

  const cgcGrades = [
    { value: 'CGC 10', label: 'CGC 10 - Pristine' },
    { value: 'CGC 9.5', label: 'CGC 9.5 - Gem Mint' },
    { value: 'CGC 9', label: 'CGC 9 - Mint' },
    { value: 'CGC 8.5', label: 'CGC 8.5 - NM-MT+' },
    { value: 'CGC 8', label: 'CGC 8 - NM-MT' },
  ];

  const bgsGrades = [
    { value: 'BGS 10', label: 'BGS 10 - Pristine' },
    { value: 'BGS 9.5', label: 'BGS 9.5 - Gem Mint' },
    { value: 'BGS 9', label: 'BGS 9 - Mint' },
    { value: 'BGS 8.5', label: 'BGS 8.5 - NM-MT+' },
    { value: 'BGS 8', label: 'BGS 8 - NM-MT' },
  ];

  const aceGrades = [
    { value: 'ACE 10', label: 'ACE 10 - Gem Mint' },
    { value: 'ACE 9', label: 'ACE 9 - Mint' },
    { value: 'ACE 8', label: 'ACE 8 - NM-MT' },
    { value: 'ACE 7', label: 'ACE 7 - NM' },
    { value: 'ACE 6', label: 'ACE 6 - EX-MT' },
  ];

  const tagGrades = [
    { value: 'TAG 10', label: 'TAG 10 - Gem Mint' },
    { value: 'TAG 9', label: 'TAG 9 - Mint' },
    { value: 'TAG 8', label: 'TAG 8 - NM-MT' },
    { value: 'TAG 7', label: 'TAG 7 - NM' },
    { value: 'TAG 6', label: 'TAG 6 - EX-MT' },
  ];

  // Generic grades for "Other" custom company
  const otherGrades = [
    { value: '10', label: '10 - Gem Mint' },
    { value: '9.5', label: '9.5 - Gem Mint' },
    { value: '9', label: '9 - Mint' },
    { value: '8.5', label: '8.5 - NM-MT+' },
    { value: '8', label: '8 - NM-MT' },
    { value: '7', label: '7 - NM' },
    { value: '6', label: '6 - EX-MT' },
  ];

  const getGradeOptions = () => {
    if (gradeType === 'raw') return rawConditions;
    if (gradingCompany === 'PSA') return psaGrades;
    if (gradingCompany === 'CGC') return cgcGrades;
    if (gradingCompany === 'BGS') return bgsGrades;
    if (gradingCompany === 'ACE') return aceGrades;
    if (gradingCompany === 'TAG') return tagGrades;
    if (gradingCompany === 'Other') return otherGrades;
    return bgsGrades;
  };

  const openGradePicker = () => {
    setSelectedGrade(card?.grade || 'NM');
    setGradePickerOpen(true);
  };

  const handleGradeConfirm = async () => {
    if (!cardId || savingGrade || !selectedGrade) return;

    // For "Other" company, prepend custom company name to grade
    let finalGrade = selectedGrade;
    if (gradingCompany === 'Other' && customCompany.trim()) {
      finalGrade = `${customCompany.trim()} ${selectedGrade}`;
    }

    if (finalGrade === card?.grade) {
      setGradePickerOpen(false);
      return;
    }

    setSavingGrade(true);
    const oldGrade = card.grade;

    // Optimistic update
    setCard(prev => ({ ...prev, grade: finalGrade }));
    setGradePickerOpen(false);

    try {
      const { error } = await supabase
        .from('cards')
        .update({ grade: finalGrade, updated_at: new Date().toISOString() })
        .eq('id', cardId);

      if (error) throw error;
    } catch (err) {
      console.error('Error updating grade:', err);
      // Revert on error
      setCard(prev => ({ ...prev, grade: oldGrade }));
    } finally {
      setSavingGrade(false);
    }
  };

  // Variant picker functions
  const openVariantPicker = async () => {
    setSelectedVariant(card?.variant || 'Normal');

    // Fetch available variants from TCGdex if we have a card_id
    if (card?.card_id) {
      try {
        const res = await fetch(`https://api.tcgdex.net/v2/en/cards/${card.card_id}`);
        if (res.ok) {
          const data = await res.json();
          const variants = data.variants || {};
          const available = [];

          if (variants.normal) available.push({ value: 'Normal', label: 'Normal' });
          if (variants.holo) available.push({ value: 'Holo', label: 'Holo' });
          if (variants.reverse) available.push({ value: 'Reverse', label: 'Reverse Holo' });
          if (variants.firstEdition) available.push({ value: '1st Edition', label: '1st Edition' });
          if (variants.wPromo) available.push({ value: 'W Promo', label: 'W Promo' });

          // Print type variants only (not physical specs)
          const printVariants = [
            { value: 'Cosmos Holo', label: 'Cosmos Holo' },
            { value: 'Cracked Ice Holo', label: 'Cracked Ice Holo' },
          ];

          if (available.length > 0) {
            setAvailableVariants([...available, ...printVariants]);
          } else {
            setAvailableVariants([
              { value: 'Normal', label: 'Normal' },
              { value: 'Holo', label: 'Holo' },
              { value: 'Reverse', label: 'Reverse Holo' },
              { value: '1st Edition', label: '1st Edition' },
              { value: 'W Promo', label: 'W Promo' },
              ...printVariants,
            ]);
          }
        }
      } catch (err) {
        console.error('Error fetching variants:', err);
        // Fallback to all options
        setAvailableVariants([
          { value: 'Normal', label: 'Normal' },
          { value: 'Holo', label: 'Holo' },
          { value: 'Reverse', label: 'Reverse Holo' },
          { value: '1st Edition', label: '1st Edition' },
          { value: 'W Promo', label: 'W Promo' },
          { value: 'Cosmos Holo', label: 'Cosmos Holo' },
          { value: 'Cracked Ice Holo', label: 'Cracked Ice Holo' },
        ]);
      }
    } else {
      // No card_id, show all options
      setAvailableVariants([
        { value: 'Normal', label: 'Normal' },
        { value: 'Holo', label: 'Holo' },
        { value: 'Reverse', label: 'Reverse Holo' },
        { value: '1st Edition', label: '1st Edition' },
        { value: 'W Promo', label: 'W Promo' },
      ]);
    }

    setVariantPickerOpen(true);
  };

  const handleVariantConfirm = async () => {
    if (!cardId || savingVariant || !selectedVariant) return;
    if (selectedVariant === card?.variant) {
      setVariantPickerOpen(false);
      return;
    }

    setSavingVariant(true);
    const oldVariant = card.variant;

    // Optimistic update
    setCard(prev => ({ ...prev, variant: selectedVariant }));
    setVariantPickerOpen(false);

    try {
      const { error } = await supabase
        .from('cards')
        .update({ variant: selectedVariant, updated_at: new Date().toISOString() })
        .eq('id', cardId);

      if (error) throw error;
    } catch (err) {
      console.error('Error updating variant:', err);
      // Revert on error
      setCard(prev => ({ ...prev, variant: oldVariant }));
    } finally {
      setSavingVariant(false);
    }
  };

  // Remove post (timer stays - user can't repost until cooldown ends)
  const handleRemovePost = async () => {
    if (!currentUserId || !cardId) return;

    try {
      // Find the most recent post containing this card by this user
      const { data: postData } = await supabase
        .from('posts_cards')
        .select('post_id, posts!posts_cards_post_id_fkey(id, user_id)')
        .eq('card_id', cardId);

      if (postData && postData.length > 0) {
        // Find posts by current user
        const userPosts = postData.filter(p => p.posts?.user_id === currentUserId);

        for (const post of userPosts) {
          // Delete from posts_cards junction table
          await supabase
            .from('posts_cards')
            .delete()
            .eq('post_id', post.post_id)
            .eq('card_id', cardId);

          // Check if this post has any other cards
          const { data: remainingCards } = await supabase
            .from('posts_cards')
            .select('id')
            .eq('post_id', post.post_id);

          // If no cards left, delete the post itself
          if (!remainingCards || remainingCards.length === 0) {
            await supabase
              .from('posts')
              .delete()
              .eq('id', post.post_id);
          }
        }
      }

      // Note: Timer (daysUntilRepost) stays as-is - we don't reset it
      // This prevents users from gaming the system by deleting and reposting
    } catch (err) {
      console.error('Error removing post:', err);
    }
  };

  // Toggle trading status between TRADE and PC
  const handleToggleTradingStatus = async () => {
    if (!cardId) return;

    const newStatus = card.trading_status === 'TRADE' ? 'PC' : 'TRADE';

    // Optimistic update
    setCard(prev => ({ ...prev, trading_status: newStatus }));

    try {
      await supabase
        .from('cards')
        .update({ trading_status: newStatus })
        .eq('id', cardId);
    } catch (err) {
      console.error('Error updating trading status:', err);
      // Revert on error
      setCard(prev => ({ ...prev, trading_status: card.trading_status }));
    }
  };

  // Open trade confirmation modal with nearest location lookup
  const handleTradeButtonClick = async () => {
    if (requireAuth('send a trade request')) return;
    // Viewer-only restriction: Block trading for minors without parent permission
    if (isViewerOnly) {
      setShowParentModal(true);
      return;
    }

    if (!currentUserId || !card || sendingRequest || tradeRequestSent) return;

    // Skip location check if user dismissed it
    if (localStorage.getItem('hideTradeDistanceWarning') === 'true') {
      setTradeConfirmOpen(true);
      return;
    }

    // Find nearest shared location
    setTradeConfirmOpen(true);
    setNearestLocation(null);
    setLoadingLocation(true);

    try {
      const myLat = activeProfile?.latitude;
      const myLng = activeProfile?.longitude;
      const theirLat = cardOwner?.latitude;
      const theirLng = cardOwner?.longitude;

      if (myLat && myLng && theirLat && theirLng && cardOwner?.location_sharing_enabled) {
        const { data: locations } = await supabase
          .from('locations')
          .select('id, name, latitude, longitude')
          .not('latitude', 'is', null)
          .not('longitude', 'is', null)
          .limit(200);

        if (locations && locations.length > 0) {
          const R = 3959;
          const haversine = (lat1, lon1, lat2, lon2) => {
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLon = (lon2 - lon1) * Math.PI / 180;
            const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) ** 2;
            return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          };

          let best = null;
          for (const loc of locations) {
            const distFromMe = haversine(myLat, myLng, loc.latitude, loc.longitude);
            const distFromThem = haversine(theirLat, theirLng, loc.latitude, loc.longitude);
            const maxDist = Math.max(distFromMe, distFromThem);
            if (!best || maxDist < best.maxDist) {
              best = {
                name: loc.name,
                distFromMe: Math.round(distFromMe),
                distFromThem: Math.round(distFromThem),
                maxDist: Math.round(maxDist),
              };
            }
          }
          if (best) setNearestLocation(best);
        }
      }
    } catch (err) {
      console.log('Location lookup failed:', err);
    } finally {
      setLoadingLocation(false);
    }
  };

  // Execute trade request after confirmation
  const executeSendTradeRequest = async () => {
    if (!currentUserId || !card || sendingRequest || tradeRequestSent) return;

    setTradeConfirmOpen(false);
    setSendingRequest(true);
    try {
      // Check for existing request first (race condition protection)
      const { data: existingRequest } = await supabase
        .from('trade_requests')
        .select('id')
        .eq('requester_id', currentUserId)
        .eq('card_id', cardId)
        .in('status', ['pending', 'accepted'])
        .maybeSingle();

      if (existingRequest) {
        setTradeRequestSent(true);
        return;
      }

      // Insert new trade request
      const { error } = await supabase
        .from('trade_requests')
        .insert({
          requester_id: currentUserId,
          card_owner_id: card.user_id,
          card_id: cardId,
          status: 'pending',
          viewed_by_owner: false,
        });

      if (error) {
        console.error('Error sending trade request:', error);
        alert('Failed to send trade request. Please try again.');
      } else {
        setTradeRequestSent(true);
        handleTradeRequestSent(); // Trigger first trade request tip if applicable

        // Send push notification to card owner
        try {
          const { data: requesterData } = await supabase
            .from('users')
            .select('username')
            .eq('id', currentUserId)
            .single();
          const requesterName = requesterData?.username || 'Someone';
          sendTradeRequestNotification(card.user_id, requesterName, card.name);
        } catch (notifErr) {
          console.log('Notification send failed:', notifErr);
        }
      }
    } catch (err) {
      console.error('Error sending trade request:', err);
      alert('Failed to send trade request. Please try again.');
    } finally {
      setSendingRequest(false);
    }
  };

  // Get all available photos for the card (user photos only, no digital)
  const getCardPhotos = () => {
    if (!card) return [];
    const photos = [];

    // Main photo (user's original photo, not digital)
    if (card.original_image_url) {
      photos.push({ url: card.original_image_url, label: 'Photo' });
    }
    // Additional photos
    if (card.back_photo) {
      photos.push({ url: card.back_photo, label: 'Back' });
    }
    if (card.front_top_photo) {
      photos.push({ url: card.front_top_photo, label: 'Front Top' });
    }
    if (card.front_bottom_photo) {
      photos.push({ url: card.front_bottom_photo, label: 'Front Bottom' });
    }
    if (card.back_top_photo) {
      photos.push({ url: card.back_top_photo, label: 'Back Top' });
    }
    if (card.back_bottom_photo) {
      photos.push({ url: card.back_bottom_photo, label: 'Back Bottom' });
    }
    if (card.other_photo_1) {
      photos.push({ url: card.other_photo_1, label: 'Other 1' });
    }
    if (card.other_photo_2) {
      photos.push({ url: card.other_photo_2, label: 'Other 2' });
    }

    // Digital image always last
    if (card.digital_image_url) {
      photos.push({ url: card.digital_image_url, label: 'Digital' });
    }

    return photos;
  };

  const cardPhotos = card ? getCardPhotos() : [];
  const hasMultiplePhotos = cardPhotos.length > 1;

  // Initialize styles with theme
  const componentStyles = styles(theme, isDark);

  if (loading) {
    return (
      <div style={componentStyles.loadingContainer}>
        <div style={componentStyles.spinner} />
      </div>
    );
  }

  if (!card) {
    return (
      <div style={componentStyles.errorContainer}>
        <p style={componentStyles.errorText}>Card not found</p>
        <button style={componentStyles.backButton} onClick={handleBack}>
          Go Back
        </button>
      </div>
    );
  }

  // Card is hidden (under review by admin)
  if (card.isHidden) {
    return (
      <div style={componentStyles.errorContainer}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p style={{ ...componentStyles.errorText, marginBottom: 8 }}>Card Under Review</p>
        <p style={{ color: theme.textMuted, fontSize: 14, textAlign: 'center', marginBottom: 20 }}>
          This card has been reported and is being reviewed by our team.
        </p>
        <button style={componentStyles.backButton} onClick={handleBack}>
          Go Back
        </button>
      </div>
    );
  }

  const imageUrl = card.digital_image_url || card.original_image_url;

  // Hide main content when coming from external and photo viewer is open
  const hideMainContent = cameFromExternal && photoViewerOpen;

  return (
    <div style={{
      ...componentStyles.container,
      ...(hideMainContent ? { backgroundColor: theme.bgPure } : {}),
    }}>
      {/* Main content */}
      <div style={hideMainContent ? { display: 'none' } : { height: '100%' }}>
      {/* Header */}
      <div style={componentStyles.header}>
        <button style={componentStyles.headerBackBtn} onClick={handleBack}>
          <BackIcon />
        </button>
        <div style={componentStyles.headerCenter}>
          <h1 style={componentStyles.headerTitle}>{card.name}</h1>
          <div style={componentStyles.headerMeta}>
            <span style={componentStyles.headerSet}>{card.set_name}</span>
            {card.set_number && <span style={componentStyles.headerNumber}>#{card.set_number}</span>}
          </div>
        </div>
        <div style={componentStyles.menuWrapper}>
          <button style={componentStyles.headerMenuBtn} onClick={() => setMenuOpen(true)}>
            <MoreVerticalIcon />
          </button>
          <CardOptionsMenu
            isOpen={menuOpen}
            onClose={() => setMenuOpen(false)}
            isFollowing={isFollowing}
            onToggleFollow={handleToggleFollow}
            onReportCard={handleReportCard}
            onBlock={handleBlockUser}
            isOwnCard={isOwnCard}
            theme={theme}
          />
        </div>
      </div>

      {/* Card Navigation Bar - shows when navigating through a list */}
      {(navInfo.hasPrev || navInfo.hasNext) && (
        <div style={componentStyles.cardNavBar}>
          {navInfo.hasPrev ? (
            <button style={componentStyles.cardNavBtn} onClick={handleNavigatePrev}>
              <ChevronLeftIcon size={14} />
              <span style={componentStyles.cardNavText}>Prev</span>
            </button>
          ) : (
            <div style={componentStyles.cardNavPlaceholder} />
          )}

          <span style={componentStyles.cardNavCounter}>{navInfo.currentIndex + 1} of {navInfo.total}</span>

          {navInfo.hasNext ? (
            <button style={componentStyles.cardNavBtn} onClick={handleNavigateNext}>
              <span style={componentStyles.cardNavText}>Next</span>
              <ChevronRightIcon size={14} />
            </button>
          ) : (
            <div style={componentStyles.cardNavPlaceholder} />
          )}
        </div>
      )}

      {/* Content */}
      <div style={componentStyles.content}>
        {/* Top Section - Card + Info Side by Side */}
        <div style={componentStyles.topSection}>
          {/* Card Image */}
          <div
            style={componentStyles.cardImageWrapper}
            onClick={() => {
              setSelectedPhotoIndex(0);
              setPhotoViewerOpen(true);
            }}
          >
            <CardImage
              digitalImage={card.digital_image_url}
              userPhoto={card.original_image_url}
              name={card.name}
              number={card.number}
              setName={card.set_name}
              theme={theme}
              style={componentStyles.cardImage}
            />
            <div style={componentStyles.zoomHint}>
              <ZoomInIcon />
            </div>
          </div>

          {/* Quick Info */}
          <div style={componentStyles.quickInfo}>
            {isTcgCard ? (
              /* TCGdex card info for wish list */
              <>
                <div style={componentStyles.infoRow}>
                  <span style={componentStyles.infoLabel}>Browsing</span>
                  <span style={{
                    ...componentStyles.statusBadge,
                    backgroundColor: 'rgba(250, 204, 21, 0.15)',
                    color: '#facc15',
                  }}>
                    Wish List
                  </span>
                </div>
                {card.hp && (
                  <div style={componentStyles.infoRow}>
                    <span style={componentStyles.infoLabel}>HP</span>
                    <span style={componentStyles.infoValue}>{card.hp}</span>
                  </div>
                )}
                {card.types && card.types.length > 0 && (
                  <div style={componentStyles.infoRow}>
                    <span style={componentStyles.infoLabel}>Type</span>
                    <span style={componentStyles.infoValue}>{card.types.join(', ')}</span>
                  </div>
                )}
                {card.rarity && (
                  <div style={componentStyles.infoRow}>
                    <span style={componentStyles.infoLabel}>Rarity</span>
                    <span style={componentStyles.infoValue}>{card.rarity}</span>
                  </div>
                )}
              </>
            ) : (
              /* Regular card info */
              <>
                {/* Condition */}
                <div
                  style={{
                    ...componentStyles.infoRow,
                    ...(isOwnCard ? componentStyles.infoRowTappable : {}),
                  }}
                  onClick={isOwnCard ? openGradePicker : undefined}
                >
                  <span style={componentStyles.infoLabel}>Condition</span>
                  <div style={componentStyles.infoValueRow}>
                    <span style={componentStyles.infoValue}>{card.grade || 'NM'}</span>
                    {isOwnCard && <ChevronDownIcon style={{ width: 16, height: 16, color: '#6a6a6a' }} />}
                  </div>
                </div>

                {/* Pricing moved to Chef's Kitchen section below */}
                {pricingEnabled && false && (
                  <div style={{ padding: '0', marginTop: '-4px', marginBottom: '4px' }}>
                    {/* Condition/Grade Ladder */}
                    {!isGradedCard(card.grade) && priceRow.stats && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {['nm', 'lp', 'mp', 'hp', 'dmg', 'all'].map(key => {
                          const label = { nm: 'NM', lp: 'LP', mp: 'MP', hp: 'HP', dmg: 'DMG', all: 'All' }[key];
                          const data = priceRow.stats[key];
                          if (!data?.med && !data?.avg) return null;
                          const p = data.med ?? data.avg;
                          const isActive = key === 'all'
                            ? !['nm','lp','mp','hp','dmg'].some(k => priceRow.stats[k])
                            : (card.grade || 'NM').toUpperCase() === label;
                          return (
                            <div key={key} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              backgroundColor: isActive ? 'rgba(74, 222, 128, 0.1)' : 'transparent',
                              border: isActive ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid transparent',
                            }}>
                              <span style={{ fontSize: '12px', color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? '600' : '400' }}>
                                {label}{data.sales ? ` (${data.sales})` : ''}
                              </span>
                              <span style={{ fontSize: '12px', color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? '600' : '400' }}>
                                {p != null ? formatPrice(p) : 'N/A'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {isGradedCard(card.grade) && priceRow.stats && (() => {
                      const stats = priceRow.stats;
                      const tiers = Object.entries(stats)
                        .filter(([key]) => key !== 'all' && key !== 'raw' && key !== 'unknown')
                        .sort((a, b) => {
                          const numA = parseFloat(a[0].replace(/[^0-9.]/g, '')) || 0;
                          const numB = parseFloat(b[0].replace(/[^0-9.]/g, '')) || 0;
                          return numB - numA;
                        });
                      if (tiers.length === 0) return null;
                      const activeGrade = (card.grade || '').trim().toUpperCase();
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {tiers.map(([key, data]) => {
                            const p = data.med ?? data.avg;
                            const isActive = key.toUpperCase() === activeGrade;
                            return (
                              <div key={key} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                backgroundColor: isActive ? 'rgba(74, 222, 128, 0.1)' : 'transparent',
                                border: isActive ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid transparent',
                              }}>
                                <span style={{ fontSize: '12px', color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? '600' : '400' }}>
                                  {key}{data.sales ? ` (${data.sales})` : ''}
                                </span>
                                <span style={{ fontSize: '12px', color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? '600' : '400' }}>
                                  {p != null ? formatPrice(p) : 'N/A'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Refresh Row */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '8px',
                      padding: '0 8px',
                    }}>
                      <span style={{ fontSize: '11px', color: theme.textMuted }}>
                        Updated {daysSinceRefresh(priceRow.last_refreshed_at)}
                      </span>
                      <button
                        onClick={async () => {
                          if (refreshingPrice || isWithinCooldown(priceRow.last_refreshed_at)) return;
                          setRefreshingPrice(true);
                          await fetchAndCacheSingleCard({
                            name: card.name,
                            setName: card.set_name || '',
                            setNumber: card.set_number || card.card_id?.split('-').pop() || '',
                            variant: card.variant || '',
                            grade: card.grade || 'NM',
                          });
                          const newRow = await getCardPriceRow(
                            card.name,
                            card.set_name || '',
                            card.set_number || card.card_id?.split('-').pop() || '',
                          );
                          if (newRow) {
                            setPriceRow(newRow);
                            const price = getPriceForCard(newRow.stats, card.grade || 'NM', card.set_name, null, newRow.evidence);
                            if (price != null) setCardPrice({ price });
                          }
                          setRefreshingPrice(false);
                        }}
                        disabled={isWithinCooldown(priceRow.last_refreshed_at) || refreshingPrice}
                        style={{
                          background: 'none',
                          border: '1px solid ' + (isWithinCooldown(priceRow.last_refreshed_at) ? theme.border : theme.accent),
                          borderRadius: '6px',
                          padding: '4px 10px',
                          fontSize: '11px',
                          color: isWithinCooldown(priceRow.last_refreshed_at) ? theme.textMuted : theme.accent,
                          cursor: isWithinCooldown(priceRow.last_refreshed_at) || refreshingPrice ? 'not-allowed' : 'pointer',
                          opacity: isWithinCooldown(priceRow.last_refreshed_at) ? 0.4 : 1,
                        }}
                      >
                        {refreshingPrice ? 'Refreshing...' : 'Refresh Price'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Variant */}
                {card.variant && (
                  <div
                    style={{
                      ...componentStyles.infoRow,
                      ...(isOwnCard ? componentStyles.infoRowTappable : {}),
                    }}
                    onClick={isOwnCard ? openVariantPicker : undefined}
                  >
                    <span style={componentStyles.infoLabel}>Variant</span>
                    <div style={componentStyles.infoValueRow}>
                      <span style={componentStyles.infoValue}>{card.variant}</span>
                      {isOwnCard && <ChevronDownIcon style={{ width: 16, height: 16, color: '#6a6a6a' }} />}
                    </div>
                  </div>
                )}

                {/* Specification */}
                {card.specification && (
                  <div
                    style={{
                      ...componentStyles.infoRow,
                      ...(isOwnCard ? componentStyles.infoRowTappable : {}),
                    }}
                    onClick={isOwnCard ? openVariantPicker : undefined}
                  >
                    <span style={componentStyles.infoLabel}>Specification</span>
                    <div style={componentStyles.infoValueRow}>
                      <span style={componentStyles.infoValue}>{card.specification}</span>
                      {isOwnCard && <ChevronDownIcon style={{ width: 16, height: 16, color: '#6a6a6a' }} />}
                    </div>
                  </div>
                )}

                {/* Status */}
                <div style={componentStyles.infoRow}>
                  <span style={componentStyles.infoLabel}>Status</span>
                  <span style={{
                    ...componentStyles.statusBadge,
                    ...(card.trading_status === 'TRADE' ? componentStyles.statusTrade : componentStyles.statusPC),
                  }}>
                    {card.trading_status === 'TRADE' ? 'For Trade' : 'PC'}
                  </span>
                </div>

                {/* Binder (if in one) */}
                {cardBinder && (
                  <div style={componentStyles.infoRow}>
                    <span style={componentStyles.infoLabel}>Binder</span>
                    <span style={componentStyles.infoValueSmall}>{cardBinder.name}</span>
                  </div>
                )}

                {/* Card Details Button */}
                <button
                  style={componentStyles.detailsButton}
                  onClick={() => setDetailsPopupOpen(true)}
                >
                  <span>Card Details</span>
                  <ChevronDownIcon style={{ transform: 'rotate(-90deg)' }} />
                </button>

              </>
            )}
          </div>
        </div>

        {/* Owner Info - show if not own card */}
        {!isOwnCard && cardOwner && (() => {
          let ownerDistance = null;
          if (cardOwner.latitude && cardOwner.longitude && cardOwner.location_sharing_enabled && activeProfile?.latitude && activeProfile?.longitude) {
            const R = 3959;
            const dLat = (cardOwner.latitude - activeProfile.latitude) * Math.PI / 180;
            const dLon = (cardOwner.longitude - activeProfile.longitude) * Math.PI / 180;
            const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(activeProfile.latitude * Math.PI / 180) * Math.cos(cardOwner.latitude * Math.PI / 180) *
              Math.sin(dLon / 2) ** 2;
            ownerDistance = Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
          }
          return (
            <div
              style={componentStyles.ownerInfo}
              onClick={() => navigate(`/user/${cardOwner.id}/collection`)}
            >
              <div style={componentStyles.ownerAvatar}>
                {cardOwner.avatar_url ? (
                  <img src={cardOwner.avatar_url} alt={cardOwner.username} style={componentStyles.ownerAvatarImg} />
                ) : (
                  <span style={componentStyles.ownerAvatarPlaceholder}>{cardOwner.username?.[0]?.toUpperCase()}</span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <span style={componentStyles.ownerName}>@{cardOwner.username}</span>
                {ownerDistance !== null && (
                  <span style={{ color: '#94a3b8', fontSize: 11, display: 'block', marginTop: 1 }}>
                    {ownerDistance < 5 ? '< 5' : ownerDistance} mi away
                  </span>
                )}
              </div>
              <span style={componentStyles.ownerArrow}>View</span>
            </div>
          );
        })()}

        {/* Action Cards */}
        <div style={componentStyles.actionCards}>
          {isTcgCard ? (
            /* Wish List Action for TCGdex cards */
            <div style={{
              ...componentStyles.actionCard,
              ...(isWished ? {
                backgroundColor: 'rgba(250, 204, 21, 0.08)',
                border: '1px solid rgba(250, 204, 21, 0.3)',
              } : {}),
            }}>
              <div style={componentStyles.actionCardHeader}>
                <div style={{
                  ...componentStyles.actionCardIcon,
                  backgroundColor: isWished ? '#facc15' : 'rgba(250, 204, 21, 0.15)',
                  color: isWished ? '#000' : '#facc15',
                }}>
                  {isWished ? <HeartFilledIcon /> : <HeartIcon />}
                </div>
                <span style={{
                  ...componentStyles.actionCardTitle,
                  color: isWished ? '#facc15' : '#ffffff',
                }}>
                  {isWished ? 'In Wish List' : 'Wish List'}
                </span>
              </div>
              <div style={componentStyles.actionCardContent}>
                {isOwnCard && (
                  <div style={{
                    ...componentStyles.actionCardRow,
                    marginBottom: '12px',
                  }}>
                    <span style={{ color: '#0ea5e9', fontSize: '13px', fontWeight: '500' }}>
                      You own this card
                    </span>
                  </div>
                )}
                {isWished ? (
                  <button
                    style={{
                      ...componentStyles.actionCardBtnPrimary,
                      backgroundColor: 'rgba(250, 204, 21, 0.15)',
                      border: '1px solid rgba(250, 204, 21, 0.3)',
                      color: '#facc15',
                    }}
                    onClick={handleRemoveFromWishList}
                    disabled={removingFromWishList}
                  >
                    {removingFromWishList ? 'Removing...' : 'Remove from Wish List'}
                  </button>
                ) : (
                  <button
                    style={{
                      ...componentStyles.actionCardBtnPrimary,
                      backgroundColor: '#facc15',
                      color: '#000',
                      opacity: wishListCount >= 20 ? 0.5 : 1,
                    }}
                    onClick={handleAddToWishList}
                    disabled={addingToWishList || wishListCount >= 20}
                  >
                    {wishListCount >= 20
                      ? 'Wish List Full (20/20)'
                      : addingToWishList
                        ? 'Adding...'
                        : `Add to Wish List (${wishListCount}/20)`}
                  </button>
                )}
              </div>
            </div>
          ) : isOwnCard ? (
            <>
              {/* Post Status Card */}
              <div style={componentStyles.actionCard}>
                <div style={componentStyles.actionCardHeader}>
                  <div style={componentStyles.actionCardIcon}>
                    <ShareIcon />
                  </div>
                  <span style={componentStyles.actionCardTitle}>Post Status</span>
                </div>
                <div style={componentStyles.actionCardContent}>
                  {daysUntilRepost > 0 ? (
                    <>
                      <div style={componentStyles.actionCardRow}>
                        <span style={componentStyles.actionCardLabel}>Status</span>
                        <div style={componentStyles.postedStatus}>
                          <ClockIcon />
                          <span>Posted, {daysUntilRepost}d to repost</span>
                        </div>
                      </div>
                      <div style={componentStyles.actionCardRow}>
                        <span style={componentStyles.actionCardLabel}>Remove Post</span>
                        <button
                          style={componentStyles.trashBtn}
                          onClick={handleRemovePost}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      style={componentStyles.actionCardBtnPrimary}
                      onClick={handleOpenPostModal}
                    >
                      Post This Card
                    </button>
                  )}
                </div>
              </div>

              {/* Collection Card */}
              <div style={componentStyles.actionCard}>
                <div style={componentStyles.actionCardHeader}>
                  <div style={componentStyles.actionCardIcon}>
                    <FolderIcon />
                  </div>
                  <span style={componentStyles.actionCardTitle}>Collection</span>
                </div>
                <div style={componentStyles.actionCardContent}>
                  {/* Status Toggle */}
                  <div style={componentStyles.actionCardRow}>
                    <span style={componentStyles.actionCardLabel}>Status</span>
                    <button
                      style={{
                        ...componentStyles.statusToggle,
                        ...(card.trading_status === 'TRADE' ? componentStyles.statusToggleTrade : componentStyles.statusTogglePC),
                      }}
                      onClick={handleToggleTradingStatus}
                    >
                      <RefreshIcon />
                      <span>{card.trading_status === 'TRADE' ? 'For Trade' : 'PC'}</span>
                    </button>
                  </div>

                  {/* Binder */}
                  <div style={componentStyles.actionCardRow}>
                    <span style={componentStyles.actionCardLabel}>Current Binder</span>
                    <button
                      style={componentStyles.binderSelectBtn}
                      onClick={openBinderPicker}
                    >
                      {cardBinder ? cardBinder.name : 'None'}
                      <ChevronDownIcon style={{ transform: 'rotate(-90deg)', width: 16, height: 16 }} />
                    </button>
                  </div>
                  {cardBinder && (
                    <div style={componentStyles.actionCardRow}>
                      <span style={componentStyles.actionCardLabel}>Remove from Binder</span>
                      <button
                        style={componentStyles.trashBtn}
                        onClick={handleRemoveFromBinder}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Chef's Kitchen - Full Pricing Section */}
              {pricingEnabled && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={ChefImage} alt="Chef" style={{ width: 26, height: 26 }} />
                      <span style={{ fontSize: 15, fontWeight: 700, color: theme.text }}>Chef's Kitchen</span>
                    </div>
                    {(() => {
                      const isVintage = isVintageSet(card.set_name);
                      const methodLabel = isVintage
                        ? (VINTAGE_LABELS[pricingPrefs.vintage_method] || 'Top 3 (12mo)')
                        : (MODERN_LABELS[pricingPrefs.modern_range] || '3 Month Median');
                      const categoryLabel = isVintage ? 'Vintage' : 'Modern';
                      return (
                        <button
                          onClick={() => setShowPrefsPicker(true)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                          }}
                        >
                          <span style={{ fontSize: 11, color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: 3 }}>
                            {categoryLabel} &middot; {methodLabel}
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </span>
                          {cardPrice?.price > 0 && (
                            <span style={{ fontSize: 18, fontWeight: 700, color: '#4ade80', marginLeft: 4 }}>
                              {formatPrice(cardPrice.price)}
                            </span>
                          )}
                          {!cardPrice?.price && !priceRow && (
                            <div style={{ width: 14, height: 14, border: '2px solid rgba(74,222,128,0.3)', borderTopColor: '#4ade80', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                          )}
                          {!cardPrice?.price && priceRow && priceDetailsInfo?.nearestPrice && (
                            <span style={{ fontSize: 13, color: '#f59e0b', marginLeft: 4, textAlign: 'right' }}>
                              ~{formatPrice(priceDetailsInfo.nearestPrice)}
                              <span style={{ fontSize: 10, color: theme.textMuted, display: 'block' }}>
                                {priceDetailsInfo.nearestGrade}
                              </span>
                            </span>
                          )}
                        </button>
                      );
                    })()}
                  </div>

                  {/* Prefs picker modal */}
                  {showPrefsPicker && (() => {
                    const isVintage = isVintageSet(card.set_name);
                    const options = isVintage
                      ? [{ value: 'top3', label: 'Top 3 (12mo)' }, { value: 'low3', label: 'Low 3 (12mo)' }, { value: '12avg', label: '12 Month Median' }, { value: 'alltime', label: 'All Time Median' }]
                      : [{ value: '1m', label: '1 Month' }, { value: '3m', label: '3 Months' }, { value: '6m', label: '6 Months' }, { value: '12m', label: '12 Months' }];
                    const currentValue = isVintage ? pricingPrefs.vintage_method : pricingPrefs.modern_range;
                    return (
                      <div
                        style={{ position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                        onClick={() => setShowPrefsPicker(false)}
                      >
                        <div
                          style={{ backgroundColor: theme.bgCard, borderRadius: 16, padding: 20, maxWidth: 320, width: '100%' }}
                          onClick={e => e.stopPropagation()}
                        >
                          <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 4 }}>
                            {isVintage ? 'Vintage Pricing Method' : 'Modern Price Range'}
                          </div>
                          <div style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 16 }}>
                            {isVintage ? 'Applies to all pre-2017 cards' : 'Applies to all 2017+ cards'}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {options.map(opt => (
                              <button
                                key={opt.value}
                                onClick={async () => {
                                  const newPrefs = { ...pricingPrefs };
                                  if (isVintage) newPrefs.vintage_method = opt.value;
                                  else newPrefs.modern_range = opt.value;
                                  setPricingPrefs(newPrefs);
                                  setShowPrefsPicker(false);
                                  if (activeProfile?.id) {
                                    await supabase.from('users').update({ pricing_prefs: newPrefs }).eq('id', activeProfile.id);
                                  }
                                  if (priceRow?.evidence) {
                                    const newPrice = getPriceForCard(priceRow.stats, card.grade || 'NM', card.set_name, newPrefs, priceRow.evidence);
                                    if (newPrice != null) setCardPrice({ price: newPrice });
                                  }
                                }}
                                style={{
                                  padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500,
                                  border: currentValue === opt.value ? '1px solid ' + theme.accent : '1px solid ' + theme.border,
                                  backgroundColor: currentValue === opt.value ? theme.accent : 'transparent',
                                  color: currentValue === opt.value ? '#fff' : theme.text,
                                  cursor: 'pointer',
                                }}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* eBay Sold Data - Condition Ladder */}
                  <div style={{ fontSize: 10, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    eBay Sold Data
                  </div>

                  {/* No exact grade message */}
                  {priceDetailsInfo?.message && !cardPrice?.price && priceRow && (
                    <div style={{
                      padding: '8px 10px', borderRadius: 8, marginBottom: 8,
                      backgroundColor: 'rgba(245, 158, 11, 0.08)',
                      border: '1px solid rgba(245, 158, 11, 0.15)',
                    }}>
                      <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 500 }}>
                        {priceDetailsInfo.message}
                      </span>
                      {priceDetailsInfo.nearestGrade && (
                        <span style={{ fontSize: 12, color: theme.textSecondary }}>
                          {' '}Closest: {priceDetailsInfo.nearestGrade} at {formatPrice(priceDetailsInfo.nearestPrice)}
                        </span>
                      )}
                    </div>
                  )}

                  {!priceRow ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                      {['NM', 'LP', 'MP', 'HP', 'DMG'].map(k => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', borderRadius: 6 }}>
                          <span style={{ fontSize: 12, color: theme.textSecondary }}>{k}</span>
                          <div style={{ width: 40, height: 14, borderRadius: 4, backgroundColor: theme.border, animation: 'pulse 1.5s ease-in-out infinite' }} />
                        </div>
                      ))}
                    </div>
                  ) : priceRow.stats && !isGradedCard(card.grade) ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 12 }}>
                      {['nm', 'lp', 'mp', 'hp', 'dmg', 'all'].map(key => {
                        const label = { nm: 'NM', lp: 'LP', mp: 'MP', hp: 'HP', dmg: 'DMG', all: 'All' }[key];
                        const data = priceRow.stats[key];
                        if (!data?.med && !data?.avg) return null;
                        const p = data.med ?? data.avg;
                        const isActive = key === 'all'
                          ? !['nm','lp','mp','hp','dmg'].some(k => priceRow.stats[k])
                          : (card.grade || 'NM').toUpperCase() === label;
                        return (
                          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', borderRadius: 6, backgroundColor: isActive ? 'rgba(74, 222, 128, 0.1)' : 'transparent', border: isActive ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid transparent' }}>
                            <span style={{ fontSize: 12, color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? 600 : 400 }}>{label}{data.sales ? ` (${data.sales})` : ''}</span>
                            <span style={{ fontSize: 12, color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? 600 : 400 }}>{p != null ? formatPrice(p) : 'N/A'}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : priceRow.stats && isGradedCard(card.grade) ? (() => {
                    const tiers = Object.entries(priceRow.stats).filter(([k]) => k !== 'all' && k !== 'raw' && k !== 'unknown').sort((a, b) => (parseFloat(b[0].replace(/[^0-9.]/g, '')) || 0) - (parseFloat(a[0].replace(/[^0-9.]/g, '')) || 0));
                    if (tiers.length === 0) return null;
                    const activeGrade = (card.grade || '').trim().toUpperCase();
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 12 }}>
                        {tiers.map(([key, data]) => {
                          const p = data.med ?? data.avg;
                          const isActive = key.toUpperCase() === activeGrade;
                          return (
                            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', borderRadius: 6, backgroundColor: isActive ? 'rgba(74, 222, 128, 0.1)' : 'transparent', border: isActive ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid transparent' }}>
                              <span style={{ fontSize: 12, color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? 600 : 400 }}>{key}{data.sales ? ` (${data.sales})` : ''}</span>
                              <span style={{ fontSize: 12, color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? 600 : 400 }}>{p != null ? formatPrice(p) : 'N/A'}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })() : (
                    <div style={{ padding: '8px', marginBottom: 12 }}>
                      <span style={{ fontSize: 12, color: theme.textMuted }}>No pricing data available</span>
                    </div>
                  )}

                  {/* Refresh Row */}
                  {priceRow && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, padding: '0 8px' }}>
                      <span style={{ fontSize: 11, color: theme.textMuted }}>Updated {daysSinceRefresh(priceRow.last_refreshed_at)}</span>
                      <button onClick={async () => { if (refreshingPrice || isWithinCooldown(priceRow.last_refreshed_at)) return; setRefreshingPrice(true); await fetchAndCacheSingleCard({ name: card.name, setName: card.set_name || '', setNumber: card.set_number || card.card_id?.split('-').pop() || '', variant: card.variant || '', grade: card.grade || 'NM' }); const newRow = await getCardPriceRow(card.name, card.set_name || '', card.set_number || card.card_id?.split('-').pop() || '', card.variant || ''); if (newRow) { setPriceRow(newRow); const price = getPriceForCard(newRow.stats, card.grade || 'NM', card.set_name, null, newRow.evidence); if (price != null) setCardPrice({ price }); } setRefreshingPrice(false); }} disabled={isWithinCooldown(priceRow.last_refreshed_at) || refreshingPrice} style={{ background: 'none', border: '1px solid ' + (isWithinCooldown(priceRow.last_refreshed_at) ? theme.border : theme.accent), borderRadius: 6, padding: '4px 10px', fontSize: 11, color: isWithinCooldown(priceRow.last_refreshed_at) ? theme.textMuted : theme.accent, cursor: isWithinCooldown(priceRow.last_refreshed_at) || refreshingPrice ? 'not-allowed' : 'pointer', opacity: isWithinCooldown(priceRow.last_refreshed_at) ? 0.4 : 1 }}>{refreshingPrice ? 'Refreshing...' : 'Refresh Price'}</button>
                    </div>
                  )}

                  {/* Verify Pricing */}
                  <div style={{ fontSize: 10, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    Verify Pricing
                  </div>

                  {/* TCGPlayer */}
                  <a
                    href={`https://www.tcgplayer.com/search/pokemon/product?q=${encodeURIComponent(card.name + ' ' + (card.set_name || ''))}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 12px', borderRadius: 8, textDecoration: 'none',
                      backgroundColor: 'rgba(59, 130, 246, 0.06)',
                      border: '1px solid rgba(59, 130, 246, 0.15)',
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 22, height: 22, borderRadius: 5, backgroundColor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#fff', fontSize: 10, fontWeight: 800 }}>T</span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 500, color: theme.text }}>TCGPlayer</span>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>

                  {/* PriceCharting */}
                  <a
                    href={`https://www.pricecharting.com/search-products?q=${encodeURIComponent(card.name + ' ' + (card.set_name || '') + ' pokemon')}&type=prices`}
                    target="_blank" rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 12px', borderRadius: 8, textDecoration: 'none',
                      backgroundColor: 'rgba(34, 197, 94, 0.06)',
                      border: '1px solid rgba(34, 197, 94, 0.15)',
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 22, height: 22, borderRadius: 5, backgroundColor: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#fff', fontSize: 10, fontWeight: 800 }}>P</span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 500, color: theme.text }}>PriceCharting</span>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>

                  {/* eBay Sold */}
                  {priceRow?.search_query && (
                    <a
                      href={`https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(priceRow?.search_query)}&_sacat=0&LH_Sold=1&LH_Complete=1`}
                      target="_blank" rel="noopener noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 12px', borderRadius: 8, textDecoration: 'none',
                        backgroundColor: 'rgba(245, 158, 11, 0.06)',
                        border: '1px solid rgba(245, 158, 11, 0.15)',
                        marginBottom: 6,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 22, height: 22, borderRadius: 5, backgroundColor: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ color: '#fff', fontSize: 10, fontWeight: 800 }}>e</span>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 500, color: theme.text }}>eBay Sold Listings</span>
                      </div>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </a>
                  )}

                  {/* PokeTrace - Coming Soon */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', borderRadius: 8,
                    backgroundColor: 'rgba(139, 92, 246, 0.06)',
                    border: '1px solid rgba(139, 92, 246, 0.1)',
                    marginBottom: 6, opacity: 0.5,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 22, height: 22, borderRadius: 5, backgroundColor: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#fff', fontSize: 9, fontWeight: 800 }}>PT</span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 500, color: theme.textSecondary }}>PokeTrace</span>
                    </div>
                    <span style={{ fontSize: 10, color: theme.textMuted, fontStyle: 'italic' }}>Coming Soon</span>
                  </div>

                  {/* CardMarket (EU) - Coming Soon */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', borderRadius: 8,
                    backgroundColor: 'rgba(14, 165, 233, 0.06)',
                    border: '1px solid rgba(14, 165, 233, 0.1)',
                    opacity: 0.5,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 22, height: 22, borderRadius: 5, backgroundColor: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#fff', fontSize: 9, fontWeight: 800 }}>CM</span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 500, color: theme.textSecondary }}>CardMarket (EU)</span>
                    </div>
                    <span style={{ fontSize: 10, color: theme.textMuted, fontStyle: 'italic' }}>Coming Soon</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Trade status card */}
              {card.trading_status === 'TRADE' ? (
                <div style={tradeRequestSent ? componentStyles.actionCardTradeRequested : componentStyles.actionCardTrade}>
                  <div style={componentStyles.tradeCardContent}>
                    <div style={tradeRequestSent ? componentStyles.tradeCardIconRequested : componentStyles.tradeCardIcon}>
                      {tradeRequestSent ? <CheckIcon /> : <TradeIcon />}
                    </div>
                    <div style={componentStyles.tradeCardInfo}>
                      <span style={tradeRequestSent ? componentStyles.tradeCardTitleRequested : componentStyles.tradeCardTitle}>
                        {tradeRequestSent ? 'Request Sent!' : 'Available for Trade'}
                      </span>
                      <span style={componentStyles.tradeCardSub}>
                        {tradeRequestSent ? 'Waiting for response' : 'Tap to request a trade'}
                      </span>
                    </div>
                  </div>
                  {!tradeRequestSent && (
                    <button
                      style={componentStyles.tradeRequestBtn}
                      onClick={handleTradeButtonClick}
                      disabled={sendingRequest}
                    >
                      {sendingRequest ? 'Sending...' : 'Request Trade'}
                    </button>
                  )}
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '14px 16px',
                  backgroundColor: theme.bgCard,
                  borderRadius: 12,
                  border: `1px solid ${theme.border}`,
                }}>
                  <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    backgroundColor: 'rgba(106, 106, 106, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </div>
                  <div>
                    <span style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block' }}>
                      Personal Collection
                    </span>
                    <span style={{ color: theme.textDisabled, fontSize: 12 }}>
                      Not available for trade
                    </span>
                  </div>
                </div>
              )}

              {/* Wish List Card */}
              <div style={componentStyles.wishlistCardContainer}>
                <div
                  style={{
                    ...componentStyles.actionCardWishlist,
                    ...(isWished ? {
                      backgroundColor: 'rgba(250, 204, 21, 0.15)',
                      borderColor: 'rgba(250, 204, 21, 0.3)',
                    } : {}),
                    flex: 1,
                  }}
                  onClick={isWished ? handleRemoveFromWishList : handleAddToWishList}
                >
                  <div style={{
                    ...componentStyles.wishlistIcon,
                    backgroundColor: isWished ? '#facc15' : 'rgba(250, 204, 21, 0.15)',
                    color: isWished ? '#000' : '#facc15',
                  }}>
                    {isWished ? <HeartFilledIcon /> : <HeartIcon />}
                  </div>
                  <span style={{
                    ...componentStyles.wishlistText,
                    color: isWished ? '#facc15' : '#ffffff',
                  }}>
                    {addingToWishList ? 'Adding...'
                      : removingFromWishList ? 'Removing...'
                      : isWished ? 'In Wish List'
                      : wishListCount >= 20 ? 'Wish List Full'
                      : 'Add to Wish List'}
                  </span>
                </div>
                <button
                  style={componentStyles.viewWishListBtn}
                  onClick={() => navigate('/collection?tab=wishlist&set=all')}
                >
                  View List
                </button>
              </div>
            </>
          )}

          {/* Discovery Card - always shown */}
          <button
            style={componentStyles.actionCardSearch}
            onClick={handleSearchThisCard}
          >
            <SearchIcon />
            <span>Find more of this card</span>
          </button>
        </div>

      </div>
      </div>{/* End main content wrapper */}

      {/* Photo Viewer Modal */}
      <AnimatePresence>
        {photoViewerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.photoViewerOverlay}
          >
            {/* Header */}
            <div style={componentStyles.photoViewerHeader}>
              <button
                style={componentStyles.photoViewerCloseBtn}
                onClick={handleClosePhotoViewer}
              >
                <CloseIcon />
              </button>

              <span style={componentStyles.photoViewerTitle}>
                {cardPhotos[selectedPhotoIndex]?.label || 'Photo'}
              </span>

              {/* Grid/Zoom Toggle - only show if multiple photos */}
              {hasMultiplePhotos && (
                <button
                  style={componentStyles.photoViewerModeBtn}
                  onClick={() => setPhotoViewMode(photoViewMode === 'zoom' ? 'grid' : 'zoom')}
                >
                  {photoViewMode === 'zoom' ? <GridIcon /> : <MaximizeIcon />}
                </button>
              )}
              {!hasMultiplePhotos && <div style={{ width: 44 }} />}
            </div>

            {/* Content */}
            {photoViewMode === 'zoom' ? (
              /* Zoom Mode - Single Image with Swipe */
              <div
                style={componentStyles.zoomModeContainer}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={() => handleTouchEnd(cardPhotos)}
              >
                {/* Left Arrow */}
                {hasMultiplePhotos && selectedPhotoIndex > 0 && (
                  <button style={componentStyles.photoNavArrowLeft} onClick={goToPrevPhoto}>
                    <ChevronLeftIcon color="#ffffff" />
                  </button>
                )}

                <TransformWrapper
                  key={selectedPhotoIndex}
                  initialScale={1}
                  minScale={1}
                  maxScale={4}
                  centerOnInit={true}
                  wheel={{ disabled: true }}
                  doubleClick={{ mode: 'toggle', step: 2 }}
                >
                  <TransformComponent>
                    <motion.img
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                      src={cardPhotos[selectedPhotoIndex]?.url}
                      alt={cardPhotos[selectedPhotoIndex]?.label}
                      style={componentStyles.zoomedImage}
                    />
                  </TransformComponent>
                </TransformWrapper>

                {/* Right Arrow */}
                {hasMultiplePhotos && selectedPhotoIndex < cardPhotos.length - 1 && (
                  <button style={componentStyles.photoNavArrowRight} onClick={() => goToNextPhoto(cardPhotos)}>
                    <ChevronRightIcon color="#ffffff" />
                  </button>
                )}

                {/* Photo Navigation Dots */}
                {hasMultiplePhotos && (
                  <div style={componentStyles.photoDots}>
                    {cardPhotos.map((_, index) => (
                      <button
                        key={index}
                        style={{
                          ...componentStyles.photoDot,
                          ...(index === selectedPhotoIndex ? componentStyles.photoDotActive : {}),
                        }}
                        onClick={() => setSelectedPhotoIndex(index)}
                      />
                    ))}
                  </div>
                )}

                {/* Photo Counter */}
                {hasMultiplePhotos && (
                  <div style={componentStyles.photoCounter}>
                    {selectedPhotoIndex + 1} / {cardPhotos.length}
                  </div>
                )}
              </div>
            ) : (
              /* Grid Mode - All Photos */
              <div style={componentStyles.gridModeContainer}>
                <div style={componentStyles.photoGrid}>
                  {cardPhotos.map((photo, index) => (
                    <div
                      key={index}
                      style={{
                        ...componentStyles.photoGridItem,
                        ...(index === selectedPhotoIndex ? componentStyles.photoGridItemActive : {}),
                      }}
                      onClick={() => {
                        setSelectedPhotoIndex(index);
                        setPhotoViewMode('zoom');
                      }}
                    >
                      <img
                        src={photo.url}
                        alt={photo.label}
                        style={componentStyles.photoGridImage}
                      />
                      <span style={componentStyles.photoGridLabel}>{photo.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Coming Soon Popup */}
      <AnimatePresence>
        {comingSoonOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.popupOverlay}
            onClick={() => setComingSoonOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              style={{
                ...componentStyles.popupModal,
                maxWidth: 280,
                textAlign: 'center',
                padding: 24,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ fontSize: 48, marginBottom: 12 }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={theme.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 3h6v3H9z" />
                  <path d="M7 6h10v2a8 8 0 0 1-2.5 5.75L12 16l-2.5-2.25A8 8 0 0 1 7 8V6z" />
                  <path d="M12 16v5" />
                  <path d="M8 21h8" />
                </svg>
              </div>
              <h3 style={{
                fontSize: 20,
                fontWeight: 600,
                color: theme.text,
                marginBottom: 8
              }}>
                Chef's Kitchen
              </h3>
              <p style={{
                fontSize: 14,
                color: theme.textMuted,
                marginBottom: 20,
                lineHeight: 1.5,
              }}>
                Coming Soon! We're cooking up tons of card information and data - pricing history, market trends, population reports, and more.
              </p>
              <button
                onClick={() => setComingSoonOpen(false)}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  backgroundColor: theme.accent,
                  border: 'none',
                  borderRadius: 10,
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report Success Modal */}
      <AnimatePresence>
        {reportSuccessOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.popupOverlay}
            onClick={() => {
              setReportSuccessOpen(false);
              navigate(-1);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              style={{
                ...componentStyles.popupModal,
                maxWidth: 320,
                textAlign: 'center',
                padding: 28,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                width: 64,
                height: 64,
                borderRadius: 32,
                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 style={{
                fontSize: 20,
                fontWeight: 600,
                color: theme.text,
                marginBottom: 8,
              }}>
                Report Submitted
              </h3>
              <p style={{
                fontSize: 14,
                color: theme.textMuted,
                marginBottom: 24,
                lineHeight: 1.5,
              }}>
                Thank you for helping keep our community safe. Our team will review this card shortly.
              </p>
              <button
                onClick={() => {
                  setReportSuccessOpen(false);
                  navigate(-1);
                }}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  backgroundColor: theme.accent,
                  border: 'none',
                  borderRadius: 12,
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Done
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Card Details Popup */}
      <AnimatePresence>
        {detailsPopupOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.popupOverlay}
            onClick={() => setDetailsPopupOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              style={componentStyles.popupModal}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={componentStyles.popupHeader}>
                <h3 style={componentStyles.popupTitle}>Card Details</h3>
                <button style={componentStyles.popupClose} onClick={() => setDetailsPopupOpen(false)}>
                  <CloseIcon />
                </button>
              </div>
              <div style={componentStyles.popupContent}>
                <div style={componentStyles.detailsGrid}>
                  {card.rarity && (
                    <div style={componentStyles.detailItem}>
                      <span style={componentStyles.detailLabel}>Rarity</span>
                      <span style={componentStyles.detailValue}>{card.rarity}</span>
                    </div>
                  )}
                  {card.hp && (
                    <div style={componentStyles.detailItem}>
                      <span style={componentStyles.detailLabel}>HP</span>
                      <span style={componentStyles.detailValue}>{card.hp}</span>
                    </div>
                  )}
                  {card.illustrator && (
                    <div style={componentStyles.detailItem}>
                      <span style={componentStyles.detailLabel}>Artist</span>
                      <span style={componentStyles.detailValue}>{card.illustrator}</span>
                    </div>
                  )}
                  {card.types && (
                    <div style={componentStyles.detailItem}>
                      <span style={componentStyles.detailLabel}>Type</span>
                      <span style={componentStyles.detailValue}>
                        {Array.isArray(card.types) ? card.types.join(', ') : card.types}
                      </span>
                    </div>
                  )}
                  {card.set_name && (
                    <div style={componentStyles.detailItem}>
                      <span style={componentStyles.detailLabel}>Set</span>
                      <span style={componentStyles.detailValue}>{card.set_name}</span>
                    </div>
                  )}
                  {card.set_number && (
                    <div style={componentStyles.detailItem}>
                      <span style={componentStyles.detailLabel}>Number</span>
                      <span style={componentStyles.detailValue}>#{card.set_number}</span>
                    </div>
                  )}
                  {card.variant && (
                    <div
                      style={{
                        ...componentStyles.detailItem,
                        ...(isOwnCard ? componentStyles.detailItemTappable : {}),
                      }}
                      onClick={isOwnCard ? openVariantPicker : undefined}
                    >
                      <span style={componentStyles.detailLabel}>Variant</span>
                      <span style={{
                        ...componentStyles.detailValue,
                        ...(isOwnCard ? { color: theme.warning } : {}),
                      }}>
                        {card.variant}
                        {isOwnCard && <span style={componentStyles.editHint}> (tap to edit)</span>}
                      </span>
                    </div>
                  )}
                  {card.grade && (
                    <div
                      style={{
                        ...componentStyles.detailItem,
                        ...(isOwnCard ? componentStyles.detailItemTappable : {}),
                      }}
                      onClick={isOwnCard ? openGradePicker : undefined}
                    >
                      <span style={componentStyles.detailLabel}>Condition</span>
                      <span style={{
                        ...componentStyles.detailValue,
                        ...(isOwnCard ? { color: theme.accent } : {}),
                      }}>
                        {card.grade}
                        {isOwnCard && <span style={componentStyles.editHint}> (tap to edit)</span>}
                      </span>
                    </div>
                  )}
                  <div style={componentStyles.detailItem}>
                    <span style={componentStyles.detailLabel}>Status</span>
                    <span style={componentStyles.detailValue}>
                      {card.trading_status === 'TRADE' ? 'For Trade' : 'Personal Collection'}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Binder Picker Popup */}
      <AnimatePresence>
        {binderPickerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.popupOverlay}
          >
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={componentStyles.binderPickerSheet}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={componentStyles.binderPickerHandle} />
              <div style={componentStyles.popupHeader}>
                <h3 style={componentStyles.popupTitle}>
                  {cardBinder ? 'Change Binder' : 'Select Binder'}
                </h3>
                <button style={componentStyles.popupClose} onClick={() => setBinderPickerOpen(false)}>
                  <CloseIcon />
                </button>
              </div>

              <div style={componentStyles.binderList}>
                {binders.length === 0 ? (
                  <p style={componentStyles.noBinders}>No binders yet. Create one first!</p>
                ) : (
                  binders.map((binder) => {
                    const isSelected = selectedBinder?.id === binder.id;
                    const isPreviewing = previewBinder?.id === binder.id;

                    return (
                      <div key={binder.id}>
                        <div style={componentStyles.binderRow}>
                          <button
                            style={{
                              ...componentStyles.binderOption,
                              ...(isSelected ? componentStyles.binderOptionSelected : {}),
                            }}
                            onClick={() => setSelectedBinder(isSelected ? null : binder)}
                          >
                            <div style={{
                              ...componentStyles.binderColor,
                              backgroundColor: binder.image === 'red' ? '#ef4444' :
                                              binder.image === 'blue' ? '#3b82f6' :
                                              binder.image === 'green' ? '#22c55e' :
                                              binder.image === 'yellow' ? '#eab308' :
                                              binder.image === 'purple' ? '#a855f7' :
                                              binder.image === 'pink' ? '#ec4899' :
                                              binder.image === 'orange' ? '#f97316' :
                                              '#6b7280',
                            }} />
                            <span style={componentStyles.binderName}>{binder.name}</span>
                            {isSelected && <span style={componentStyles.selectedCheck}>Selected</span>}
                          </button>
                          <button
                            style={{
                              ...componentStyles.quickViewBtn,
                              ...(isPreviewing ? componentStyles.quickViewBtnActive : {}),
                            }}
                            onClick={() => handleQuickView(binder)}
                          >
                            <GridIcon />
                          </button>
                        </div>

                        {/* Quick View Preview */}
                        {isPreviewing && (
                          <div style={componentStyles.previewGrid}>
                            {previewCards.length === 0 ? (
                              <span style={componentStyles.previewEmpty}>No cards yet</span>
                            ) : (
                              previewCards.map((c) => (
                                <CardImage
                                  key={c.id}
                                  digitalImage={c.digital_image_url}
                                  userPhoto={c.original_image_url}
                                  name={c.name}
                                  theme={theme}
                                  style={componentStyles.previewCard}
                                />
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Done Button */}
              <div style={componentStyles.binderPickerFooter}>
                <button
                  style={componentStyles.doneButton}
                  onClick={handleBinderDone}
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grade Picker Modal */}
      <AnimatePresence>
        {gradePickerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.gradePickerOverlay}
            onClick={() => setGradePickerOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              style={componentStyles.gradePickerModal}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={componentStyles.gradePickerHeader}>
                <h3 style={componentStyles.gradePickerTitle}>Update Condition</h3>
                <button
                  style={componentStyles.gradePickerClose}
                  onClick={() => setGradePickerOpen(false)}
                >
                  <CloseIcon />
                </button>
              </div>

              {/* Warning */}
              <p style={componentStyles.gradePickerWarning}>
                Misrepresenting card condition to mislead traders may result in negative reviews and account restrictions.
              </p>

              {/* Raw / Graded Toggle */}
              <div style={componentStyles.gradeTypeToggle}>
                <button
                  style={{
                    ...componentStyles.gradeTypeBtn,
                    ...(gradeType === 'raw' ? componentStyles.gradeTypeBtnActive : {}),
                  }}
                  onClick={() => setGradeType('raw')}
                >
                  Raw
                </button>
                <button
                  style={{
                    ...componentStyles.gradeTypeBtn,
                    ...(gradeType === 'graded' ? componentStyles.gradeTypeBtnActive : {}),
                  }}
                  onClick={() => setGradeType('graded')}
                >
                  Graded
                </button>
              </div>

              {/* Company Toggle (only for graded) */}
              {gradeType === 'graded' && (
                <>
                  <div style={componentStyles.companyToggle}>
                    {['PSA', 'CGC', 'BGS', 'ACE', 'TAG', 'Other'].map((company) => (
                      <button
                        key={company}
                        style={{
                          ...componentStyles.companyBtn,
                          ...(gradingCompany === company ? componentStyles.companyBtnActive : {}),
                        }}
                        onClick={() => setGradingCompany(company)}
                      >
                        {company}
                      </button>
                    ))}
                  </div>
                  {/* Custom company input for "Other" */}
                  {gradingCompany === 'Other' && (
                    <input
                      type="text"
                      placeholder="Enter company name (e.g., SGC)"
                      value={customCompany}
                      onChange={(e) => setCustomCompany(e.target.value.toUpperCase())}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        marginBottom: 12,
                        background: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 15,
                      }}
                    />
                  )}
                </>
              )}

              <div style={componentStyles.gradePickerOptions}>
                {getGradeOptions().map((option) => (
                  <button
                    key={option.value}
                    style={{
                      ...componentStyles.gradePickerOption,
                      ...(selectedGrade === option.value ? componentStyles.gradePickerOptionActive : {}),
                    }}
                    onClick={() => setSelectedGrade(option.value)}
                  >
                    <span>{option.label}</span>
                    {selectedGrade === option.value && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>

              {/* Done Button */}
              <div style={componentStyles.gradePickerFooter}>
                <button
                  style={componentStyles.gradePickerDoneBtn}
                  onClick={handleGradeConfirm}
                  disabled={savingGrade}
                >
                  {savingGrade ? 'Saving...' : 'Done'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Variant Picker Modal */}
      <AnimatePresence>
        {variantPickerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.gradePickerOverlay}
            onClick={() => setVariantPickerOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              style={componentStyles.gradePickerModal}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={componentStyles.gradePickerHeader}>
                <h3 style={componentStyles.gradePickerTitle}>Update Variant</h3>
                <button
                  style={componentStyles.gradePickerClose}
                  onClick={() => setVariantPickerOpen(false)}
                >
                  <CloseIcon />
                </button>
              </div>

              {/* Warning */}
              <p style={componentStyles.gradePickerWarning}>
                Changing card details affects how others see your collection. Please select the correct variant for accurate representation.
              </p>

              <div style={componentStyles.gradePickerOptions}>
                {availableVariants.map((option) => (
                  <button
                    key={option.value}
                    style={{
                      ...componentStyles.gradePickerOption,
                      ...(selectedVariant === option.value ? componentStyles.variantPickerOptionActive : {}),
                    }}
                    onClick={() => setSelectedVariant(option.value)}
                  >
                    <span>{option.label}</span>
                    {selectedVariant === option.value && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>

              {/* Specification */}
              <div style={{ marginTop: 16 }}>
                <h3 style={{ ...componentStyles.gradePickerTitle, fontSize: 14, marginBottom: 8 }}>Specification</h3>
                <div style={componentStyles.gradePickerOptions}>
                  {[{ value: '', label: 'None' }, { value: 'Shadowless', label: 'Shadowless' }, { value: 'WOTC Stamped', label: 'WOTC Stamped' }, { value: 'Prerelease', label: 'Prerelease' }, { value: 'STAFF', label: 'STAFF' }, { value: 'Winner', label: 'Winner' }, { value: 'League Stamp', label: 'League Stamp' }, { value: 'Build-A-Bear', label: 'Build-A-Bear' }, { value: 'Error/Misprint', label: 'Error/Misprint' }].map((option) => (
                    <button
                      key={option.value}
                      style={{
                        ...componentStyles.gradePickerOption,
                        ...((card?.specification || '') === option.value ? componentStyles.variantPickerOptionActive : {}),
                      }}
                      onClick={async () => {
                        const spec = option.value || null;
                        await supabase.from('cards').update({ specification: spec }).eq('id', card.id);
                        setCard(prev => ({ ...prev, specification: spec }));
                      }}
                    >
                      <span>{option.label}</span>
                      {(card?.specification || '') === option.value && (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Done Button */}
              <div style={componentStyles.gradePickerFooter}>
                <button
                  style={componentStyles.variantPickerDoneBtn}
                  onClick={handleVariantConfirm}
                  disabled={savingVariant}
                >
                  {savingVariant ? 'Saving...' : 'Done'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report Card Modal */}
      <ReportCardModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        cardId={cardId}
        currentUserId={currentUserId}
        onSuccess={handleReportSuccess}
        theme={theme}
      />

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={postModalOpen}
        onClose={() => setPostModalOpen(false)}
        userId={currentUserId}
        userCards={userCards}
        preSelectedCard={card}
        onSuccess={() => {
          // Reset repost cooldown after successful post
          setLastPostDate(new Date());
          setDaysUntilRepost(7);
        }}
      />

      {/* Trade Confirmation Modal */}
      <AnimatePresence>
        {tradeConfirmOpen && card && (() => {
          const tcStyles = tradeConfirmStyles(theme);
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={tcStyles.overlay}
              onClick={() => setTradeConfirmOpen(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                style={tcStyles.modal}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 style={tcStyles.title}>Request Trade?</h3>
                <div style={tcStyles.cardPreview}>
                  <CardImage
                    digitalImage={card.digital_image_url}
                    userPhoto={card.original_image_url}
                    name={card.name}
                    theme={theme}
                    style={tcStyles.cardImage}
                  />
                  <div style={tcStyles.cardInfo}>
                    <span style={tcStyles.cardName}>{card.name}</span>
                    <span style={tcStyles.ownerName}>
                      from {cardOwner?.username || 'this user'}
                    </span>
                  </div>
                </div>
                {/* Nearest location info */}
                {!loadingLocation && nearestLocation && (
                  <div style={{
                    padding: '10px 14px',
                    backgroundColor: 'rgba(14, 165, 233, 0.1)',
                    borderRadius: 10,
                    marginBottom: 14,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <span style={{ color: '#0ea5e9', fontSize: 12, fontWeight: 600 }}>Nearest Trainer Center</span>
                    </div>
                    <span style={{ color: theme.text, fontSize: 13, fontWeight: 600, display: 'block' }}>
                      {nearestLocation.name}
                    </span>
                    <span style={{ color: theme.textSecondary, fontSize: 11 }}>
                      {nearestLocation.distFromMe < 5 ? '< 5' : nearestLocation.distFromMe} mi from you
                      {' / '}
                      {nearestLocation.distFromThem < 5 ? '< 5' : nearestLocation.distFromThem} mi from them
                    </span>
                  </div>
                )}
                {loadingLocation && (
                  <div style={{ textAlign: 'center', padding: '8px 0 14px', color: theme.textSecondary, fontSize: 12 }}>
                    Finding nearest Trainer Center...
                  </div>
                )}

                {/* Don't show again checkbox */}
                {nearestLocation && (
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 14,
                    cursor: 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          localStorage.setItem('hideTradeDistanceWarning', 'true');
                        } else {
                          localStorage.removeItem('hideTradeDistanceWarning');
                        }
                      }}
                      style={{ width: 16, height: 16, accentColor: '#0ea5e9' }}
                    />
                    <span style={{ color: theme.textSecondary, fontSize: 11 }}>Don't show distance info again</span>
                  </label>
                )}

                <div style={tcStyles.buttons}>
                  <button
                    style={tcStyles.cancelBtn}
                    onClick={() => setTradeConfirmOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    style={tcStyles.confirmBtn}
                    onClick={executeSendTradeRequest}
                  >
                    Send Request
                  </button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* First Trade Request Tip - shows after sending first trade request */}
      <TutorialBackdrop
        visible={currentStep === TUTORIAL_STEPS.FIRST_TRADE_REQUEST_TIP}
      />
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.FIRST_TRADE_REQUEST_TIP}
        mode="normal"
        characterImage={ChaseWaveImage}
        message={[
          "There you go!",
          "",
          "Let's see if they're interested",
          "in your collection!",
          "Here's a tip..."
        ]}
        hideButtons={false}
        yesText="Next"
        noText=""
        onYes={() => {
          handleFirstTradeRequestNext();
          navigate('/home');
        }}
      />

      {/* Add Parent Modal for viewer-only users */}
      <AddParentModal
        isOpen={showParentModal}
        onClose={() => setShowParentModal(false)}
        feature="trading"
      />

    </div>
  );
};

const styles = (theme, isDark) => ({
  container: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.bg,
    background: isDark ? theme.bg : `linear-gradient(180deg, ${theme.bgCard} 0%, ${theme.bg} 150px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: `3px solid ${theme.border}`,
    borderTopColor: theme.accent,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.bg,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  errorText: {
    color: theme.textMuted,
    fontSize: '16px',
  },
  backButton: {
    padding: '12px 24px',
    borderRadius: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.border}`,
    flexShrink: 0,
    gap: '12px',
  },
  headerBackBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.text,
    flexShrink: 0,
  },
  headerCenter: {
    flex: 1,
    textAlign: 'center',
    overflow: 'hidden',
  },
  headerTitle: {
    color: theme.text,
    fontSize: '16px',
    fontWeight: '700',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '2px',
  },
  headerSet: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  headerNumber: {
    color: '#0ea5e9',
    fontSize: '12px',
    fontWeight: '600',
  },
  menuWrapper: {
    position: 'relative',
    flexShrink: 0,
  },
  headerMenuBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textMuted,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    paddingBottom: '100px',
  },
  topSection: {
    display: 'flex',
    gap: '16px',
    marginBottom: '20px',
  },
  cardImageWrapper: {
    position: 'relative',
    flex: '0 0 140px',
    cursor: 'pointer',
  },
  cardImage: {
    width: '100%',
    height: 'auto',
    aspectRatio: '245 / 342',
    borderRadius: '14px',
    objectFit: 'cover',
    boxShadow: isDark
      ? '0 4px 16px rgba(0, 0, 0, 0.4)'
      : '0 8px 32px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08)',
    border: isDark ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
  },
  zoomHint: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    width: '28px',
    height: '28px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
  },
  quickInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoLabel: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  infoValue: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
  },
  infoValueSmall: {
    color: theme.textMuted,
    fontSize: '13px',
  },
  ebayLink: {
    color: '#3b82f6',
    fontSize: '14px',
    fontWeight: '600',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  infoRowTappable: {
    cursor: 'pointer',
    padding: '8px 0',
    margin: '-8px 0',
    borderRadius: '8px',
  },
  infoValueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  statusTrade: {
    backgroundColor: 'rgba(34, 197, 94, 0.2)',
    color: '#4ade80',
  },
  statusPC: {
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    color: '#a78bfa',
  },
  detailsButton: {
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    backgroundColor: theme.bgCard,
    border: isDark ? `1px solid ${theme.border}` : '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: '12px',
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    boxShadow: isDark ? 'none' : '0 1px 4px rgba(0, 0, 0, 0.04)',
  },
  chefsLabButton: {
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 14px',
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#000',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
  },
  ownerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '16px',
    padding: '12px 16px',
    backgroundColor: theme.bgSheet,
    borderRadius: '14px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    border: isDark ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: isDark ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  ownerAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: theme.border,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  ownerAvatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  ownerAvatarPlaceholder: {
    color: theme.textMuted,
    fontSize: '14px',
    fontWeight: '600',
  },
  ownerName: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
    flex: 1,
  },
  ownerArrow: {
    color: '#0ea5e9',
    fontSize: '13px',
    fontWeight: '500',
  },
  // Action Cards Styles
  actionCards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '20px',
  },
  actionCard: {
    backgroundColor: theme.bgSheet,
    borderRadius: '16px',
    border: isDark ? `1px solid ${theme.border}` : '1px solid rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
    boxShadow: isDark ? 'none' : '0 2px 12px rgba(0, 0, 0, 0.06)',
  },
  actionCardChef: {
    backgroundColor: 'rgba(139, 92, 246, 0.08)',
    borderRadius: '16px',
    border: '1px solid rgba(139, 92, 246, 0.2)',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  chefIcon: {
    width: '28px',
    height: '28px',
    objectFit: 'contain',
  },
  chefDescription: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  actionCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 14px',
    borderBottom: `1px solid ${theme.border}`,
  },
  actionCardIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#0ea5e9',
  },
  actionCardTitle: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
  },
  actionCardContent: {
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  actionCardRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actionCardLabel: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  actionCardBtnPrimary: {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    backgroundColor: '#0ea5e9',
    border: 'none',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  actionCardBtnDanger: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0,
  },
  postedStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    borderRadius: '6px',
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    color: '#4ade80',
    fontSize: '12px',
    fontWeight: '500',
  },
  trashBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  statusToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  statusToggleTrade: {
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    color: '#4ade80',
  },
  statusTogglePC: {
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    color: '#a78bfa',
  },
  binderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  binderSelectBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    borderRadius: '8px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    color: theme.text,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  binderRemoveBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: 'none',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  // Trade Card (for non-owners)
  actionCardTrade: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
    borderRadius: '10px',
    border: '1px solid rgba(239, 68, 68, 0.2)',
  },
  actionCardTradeRequested: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
    borderRadius: '10px',
    border: '1px solid rgba(239, 68, 68, 0.3)',
  },
  tradeCardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  tradeCardIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ef4444',
  },
  tradeCardIconRequested: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ef4444',
  },
  tradeCardInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  tradeCardTitle: {
    color: '#f87171',
    fontSize: '13px',
    fontWeight: '600',
  },
  tradeCardTitleRequested: {
    color: '#ef4444',
    fontSize: '13px',
    fontWeight: '600',
  },
  tradeCardSub: {
    color: theme.textSecondary,
    fontSize: '11px',
  },
  tradeRequestBtn: {
    padding: '8px 12px',
    borderRadius: '6px',
    backgroundColor: '#ef4444',
    border: 'none',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    whiteSpace: 'nowrap',
  },
  // Wish List Card
  actionCardWishlist: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    backgroundColor: isDark ? 'rgba(249, 115, 22, 0.08)' : 'rgba(249, 115, 22, 0.12)',
    borderRadius: '10px',
    border: isDark ? '1px solid rgba(249, 115, 22, 0.2)' : '1px solid rgba(249, 115, 22, 0.3)',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  wishlistCardContainer: {
    display: 'flex',
    gap: '8px',
    alignItems: 'stretch',
  },
  viewWishListBtn: {
    padding: '10px 14px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    color: theme.textMuted,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    WebkitTapHighlightColor: 'transparent',
  },
  wishlistIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: isDark ? 'rgba(249, 115, 22, 0.15)' : 'rgba(234, 88, 12, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: isDark ? '#f97316' : '#c2410c',
  },
  wishlistText: {
    color: isDark ? '#fb923c' : '#c2410c',
    fontSize: '13px',
    fontWeight: '600',
  },
  // Search Card
  actionCardSearch: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: theme.bgCard,
    borderRadius: '10px',
    border: `1px solid ${theme.border}`,
    color: theme.textMuted,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  // Card Navigation Bar
  cardNavBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0 16px',
    marginTop: '12px',
    padding: '12px 14px',
    backgroundColor: theme.bgSheet,
    borderRadius: '14px',
    border: isDark ? `1px solid ${theme.border}` : '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: isDark ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  cardNavBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 10px',
    backgroundColor: theme.bgCard,
    borderRadius: '8px',
    border: isDark ? `1px solid ${theme.border}` : '1px solid rgba(0, 0, 0, 0.08)',
    color: theme.accent,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    maxWidth: '45%',
    overflow: 'hidden',
  },
  cardNavText: {
    color: theme.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '11px',
  },
  cardNavCounter: {
    color: theme.textSecondary,
    fontSize: '13px',
    fontWeight: '500',
  },
  cardNavPlaceholder: {
    width: '140px',
  },
  // Photo Viewer Styles
  photoViewerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000000',
    zIndex: 2000,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)',
  },
  photoViewerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    flexShrink: 0,
  },
  photoViewerCloseBtn: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
  },
  photoViewerTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
  },
  photoViewerModeBtn: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
  },
  zoomModeContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: '40px',
    position: 'relative',
    overflow: 'hidden',
  },
  zoomedImage: {
    maxWidth: '90vw',
    maxHeight: '70vh',
    objectFit: 'contain',
    borderRadius: '16px',
    touchAction: 'pinch-zoom',
    boxShadow: isDark
      ? '0 8px 32px rgba(0, 0, 0, 0.6)'
      : '0 12px 48px rgba(0, 0, 0, 0.2)',
  },
  photoDots: {
    position: 'absolute',
    bottom: '80px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
  },
  photoDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  photoDotActive: {
    backgroundColor: '#0ea5e9',
    transform: 'scale(1.2)',
  },
  photoNavArrowLeft: {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
    zIndex: 10,
  },
  photoNavArrowRight: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
    zIndex: 10,
  },
  photoCounter: {
    position: 'absolute',
    bottom: '40px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: '#ffffff',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
  },
  gridModeContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 16px 20px',
  },
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  photoGridItem: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: theme.bgCard,
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'all 0.2s ease',
  },
  photoGridItemActive: {
    borderColor: '#0ea5e9',
  },
  photoGridImage: {
    width: '100%',
    aspectRatio: '0.714',
    objectFit: 'cover',
  },
  photoGridLabel: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '500',
    textAlign: 'center',
  },
  // Popup styles
  popupOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
  },
  popupModal: {
    backgroundColor: theme.bgSheet,
    borderRadius: '20px',
    width: '100%',
    maxWidth: '360px',
    maxHeight: '80vh',
    overflow: 'hidden',
    border: isDark ? `1px solid ${theme.border}` : '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 12px 48px rgba(0, 0, 0, 0.15)',
  },
  popupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.border}`,
  },
  popupTitle: {
    color: theme.text,
    fontSize: '17px',
    fontWeight: '600',
    margin: 0,
  },
  popupClose: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: theme.bgCard,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  popupContent: {
    padding: '20px',
    overflowY: 'auto',
    maxHeight: '60vh',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLabel: {
    color: theme.textSecondary,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detailValue: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
  },
  // Binder picker styles
  binderPickerSheet: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.bgSheet,
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    maxHeight: '70vh',
    overflow: 'hidden',
    boxShadow: isDark ? '0 -4px 24px rgba(0, 0, 0, 0.5)' : '0 -8px 32px rgba(0, 0, 0, 0.12)',
    border: isDark ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
    borderBottom: 'none',
  },
  binderPickerHandle: {
    width: '36px',
    height: '4px',
    backgroundColor: theme.borderHover,
    borderRadius: '2px',
    margin: '12px auto 0',
  },
  binderList: {
    padding: '16px',
    overflowY: 'auto',
    maxHeight: '45vh',
  },
  binderRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '10px',
  },
  binderOption: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  binderOptionSelected: {
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
    borderColor: '#0ea5e9',
  },
  quickViewBtn: {
    width: '48px',
    height: '48px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0,
  },
  quickViewBtnActive: {
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
    borderColor: '#0ea5e9',
    color: '#0ea5e9',
  },
  binderColor: {
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    flexShrink: 0,
  },
  binderName: {
    flex: 1,
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'left',
  },
  selectedCheck: {
    color: '#0ea5e9',
    fontSize: '12px',
    fontWeight: '600',
  },
  previewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '6px',
    padding: '12px',
    backgroundColor: theme.bg,
    borderRadius: '10px',
    marginBottom: '10px',
    marginTop: '-4px',
  },
  previewCard: {
    width: '100%',
    aspectRatio: '245 / 342',
    objectFit: 'cover',
    borderRadius: '4px',
  },
  previewEmpty: {
    gridColumn: '1 / -1',
    color: theme.textMuted,
    fontSize: '12px',
    textAlign: 'center',
    padding: '20px',
  },
  binderPickerFooter: {
    padding: '16px',
    borderTop: `1px solid ${theme.border}`,
  },
  doneButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#0ea5e9',
    border: 'none',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  noBinders: {
    color: theme.textSecondary,
    fontSize: '14px',
    textAlign: 'center',
    padding: '20px',
    margin: 0,
  },
  // Grade Picker Styles
  gradePickerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  gradePickerModal: {
    width: '100%',
    maxWidth: '500px',
    backgroundColor: theme.bgSheet,
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    maxHeight: '60vh',
    marginBottom: 'calc(80px + env(safe-area-inset-bottom))',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: isDark ? '0 -4px 24px rgba(0, 0, 0, 0.5)' : '0 -8px 32px rgba(0, 0, 0, 0.12)',
    border: isDark ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
    borderBottom: 'none',
  },
  gradePickerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.border}`,
  },
  gradePickerTitle: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  gradePickerClose: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: theme.bgCard,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  gradePickerOptions: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 0',
  },
  gradePickerOption: {
    width: '100%',
    padding: '14px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.text,
    fontSize: '15px',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.bgCard}`,
  },
  gradePickerOptionActive: {
    color: '#0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
  },
  variantPickerOptionActive: {
    color: theme.warning || '#f59e0b',
    backgroundColor: theme.warningBg || 'rgba(245, 158, 11, 0.1)',
  },
  variantPickerDoneBtn: {
    width: '100%',
    padding: '14px',
    backgroundColor: theme.warning || '#f59e0b',
    border: 'none',
    borderRadius: '12px',
    color: '#000',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  detailItemTappable: {
    cursor: 'pointer',
  },
  editHint: {
    fontSize: '11px',
    opacity: 0.7,
    fontWeight: '400',
  },
  gradeTypeToggle: {
    display: 'flex',
    backgroundColor: theme.bgCard,
    borderRadius: '10px',
    padding: '4px',
    margin: '0 16px 12px',
  },
  gradeTypeBtn: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    color: theme.textSecondary,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  gradeTypeBtnActive: {
    backgroundColor: '#0ea5e9',
    color: '#ffffff',
  },
  companyToggle: {
    display: 'flex',
    backgroundColor: theme.bgCard,
    borderRadius: '10px',
    padding: '4px',
    margin: '0 16px 12px',
  },
  companyBtn: {
    flex: 1,
    padding: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    color: theme.textSecondary,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  companyBtnActive: {
    backgroundColor: '#0ea5e9',
    color: '#ffffff',
  },
  gradePickerWarning: {
    color: '#f97316',
    fontSize: '12px',
    lineHeight: '1.4',
    margin: '0 16px 12px',
    padding: '10px 12px',
    backgroundColor: 'rgba(249, 115, 22, 0.1)',
    borderRadius: '8px',
    border: '1px solid rgba(249, 115, 22, 0.2)',
  },
  gradePickerFooter: {
    padding: '12px 16px',
    borderTop: `1px solid ${theme.border}`,
  },
  gradePickerDoneBtn: {
    width: '100%',
    padding: '14px',
    backgroundColor: theme.accent,
    border: 'none',
    borderRadius: '10px',
    color: theme.text,
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
});

const tradeConfirmStyles = (theme) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.overlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modal: {
    backgroundColor: theme.bgCard,
    borderRadius: '20px',
    padding: '24px',
    maxWidth: '320px',
    width: '100%',
    border: `1px solid ${theme.border}`,
  },
  title: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '700',
    margin: '0 0 20px 0',
    textAlign: 'center',
  },
  cardPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: theme.bg,
    borderRadius: '12px',
    padding: '12px',
    marginBottom: '20px',
  },
  cardImage: {
    width: '60px',
    height: '84px',
    borderRadius: '6px',
    objectFit: 'cover',
  },
  cardInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minWidth: 0,
  },
  cardName: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ownerName: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  buttons: {
    display: 'flex',
    gap: '12px',
  },
  cancelBtn: {
    flex: 1,
    padding: '14px',
    borderRadius: '12px',
    backgroundColor: theme.border,
    border: 'none',
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  confirmBtn: {
    flex: 1,
    padding: '14px',
    borderRadius: '12px',
    backgroundColor: theme.accent,
    border: 'none',
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
});

export default CardDetail;
